/**
 * Product Configurator for Minimalista Theme
 * Handles material selection by zones with dynamic pricing
 */

(function() {
  'use strict';

  // Namespace — set OnoConfigurator.debugPrice = true in console to enable price logs (then refresh page)
  window.OnoConfigurator = window.OnoConfigurator || {};

  /**
   * Parse a price string (e.g. "18,776.00" or "4.068,00") to numeric value in main currency units.
   * When both comma and dot exist: the one followed by 2 digits (cents) is the decimal separator.
   * - US: 18,776.00 → 18776.00 (comma thousands, dot decimal)
   * - EU: 4.068,00 → 4068.00 (dot thousands, comma decimal)
   */
  function parsePriceString(matchedValue) {
    if (!matchedValue || typeof matchedValue !== 'string') return NaN;
    const hasComma = matchedValue.includes(',');
    const hasDot = matchedValue.includes('.');
    if (hasComma && hasDot) {
      const afterComma = matchedValue.split(',')[1] || '';
      const afterDot = matchedValue.split('.')[1] || '';
      const commaIsDecimal = afterComma.length <= 2 && afterComma.length >= 1 && /^\d+$/.test(afterComma);
      const dotIsDecimal = afterDot.length <= 2 && afterDot.length >= 1 && /^\d+$/.test(afterDot);
      if (dotIsDecimal && !commaIsDecimal) {
        return parseFloat(matchedValue.replace(/,/g, ''));
      }
      if (commaIsDecimal && !dotIsDecimal) {
        return parseFloat(matchedValue.replace(/\./g, '').replace(',', '.'));
      }
      return parseFloat(matchedValue.replace(/,/g, ''));
    }
    if (hasComma) {
      const parts = matchedValue.split(',');
      return (parts.length === 2 && parts[1].length <= 2)
        ? parseFloat(matchedValue.replace(',', '.'))
        : parseFloat(matchedValue.replace(/,/g, ''));
    }
    if (hasDot) {
      const parts = matchedValue.split('.');
      return (parts.length === 2 && parts[1].length <= 2)
        ? parseFloat(matchedValue)
        : parseFloat(matchedValue.replace(/\./g, ''));
    }
    return parseFloat(matchedValue);
  }

  /**
   * Main ProductConfigurator class
   */
  class ProductConfigurator {
    constructor(rootEl) {
      this.rootEl = rootEl;
      this.sku = rootEl.dataset.productSku;
      this.locale = rootEl.dataset.locale || 'en';
      this.configUrl = rootEl.dataset.configUrl || 'https://cdn.onohome.com/configurator';
      this.sectionId = rootEl.dataset.sectionId;
      this.productId = rootEl.dataset.productId;
      // Variant id from Liquid (product.selected_or_first_available_variant.id). Used when form is in another section.
      const defaultVariantIdStr = rootEl.dataset.defaultVariantId;
      this._defaultVariantIdFromLiquid = defaultVariantIdStr ? parseInt(defaultVariantIdStr, 10) : null;

      // Price debug: set data-price-debug="true" on .onohome-configurator or window.OnoConfigurator.debugPrice = true
      this.priceDebug = !!(rootEl.dataset.priceDebug === 'true' || (window.OnoConfigurator && window.OnoConfigurator.debugPrice));

      // Option prices: prefer addon variant prices in current currency (from theme) so configurator total = cart total
      this.addonVariantPrices = {};
      var rateStr = rootEl.dataset.eurToLocalRate || (window.OnoConfigurator && window.OnoConfigurator.eurToLocalRate);
      this.eurToLocalRate = rateStr ? parseFloat(rateStr) : null;

      // State
      this.config = null;
      this.priceMap = null;
      this.zones = [];
      this.currentVariant = null;
      this.selectedMaterials = {}; // zoneId -> material
      this.variantPriceFromPicker = false; // when true, trust variant.price; don't overwrite from DOM (avoids stale DOM on variant switch)
      this._cachedVariantId = null; // resolved once from DOM so add-to-cart works even if form selectors miss later

      // UI elements
      this.uiRenderer = null;
      this.priceCalculator = null;
      this.zoneManager = null;

      // Event listeners
      this.variantChangeHandler = null;

      if (!this.sku) {
        this.hide();
        return;
      }
      
      // Check if element is hidden
      const computedStyle = window.getComputedStyle(rootEl);
      if (computedStyle.display === 'none') {
        // Element might be hidden by Liquid if no SKU, but we have SKU, so show it
        rootEl.style.display = '';
      }

      this.init();
    }

    async init() {
      try {
        // Get initial variant first to check if SKU needs updating
        this.updateCurrentVariant();
        
        // If variant is available and has a different SKU, update it before loading config
        if (this.currentVariant && this.currentVariant.sku && this.currentVariant.sku !== this.sku) {
          this.sku = this.currentVariant.sku;
          this.rootEl.setAttribute('data-product-sku', this.currentVariant.sku);
        }
        
        // Load config and price map in parallel
        await Promise.all([
          this.loadConfig(),
          this.loadPriceMap()
        ]);

        this.loadAddonVariantPrices();

        // If config is null (404 or error), initialize with empty zones for simple add to cart
        if (!this.config) {
          this.config = { zones: [] };
        }

        // Check if config has zones with options
        const hasConfigurableZones = this.config && this.config.zones && 
          this.config.zones.some(zone => zone.options && zone.options.length > 0);
        
        // If no configurable zones, still show configurator but with just add to cart
        if (!hasConfigurableZones) {
          // Still initialize for simple add to cart
        }

        // Initialize managers
        this.zoneManager = new ZoneManager(this.config.zones || [], this.locale);
        this.priceCalculator = new PriceCalculator(this.priceMap, this);
        this.uiRenderer = new UIRenderer(this.rootEl, this.locale, this);

        // Render UI
        this.uiRenderer.render(this.config.zones || []);

        // Listen for variant changes
        this.setupVariantListener();

        // Ensure variant is up to date after render
        this.updateCurrentVariant();
        
        // If still no variant, try again after a short delay (DOM might not be ready)
        if (!this.currentVariant) {
          setTimeout(() => {
            this.updateCurrentVariant();
            if (this.currentVariant) {
              this.recalculateTotal();
            }
          }, 500);
        }

        // Validate initial state (only if there are configurable zones)
        if (hasConfigurableZones) {
          this.validateSelections();
        } else {
          // Enable add to cart if no configurable zones
          this.uiRenderer.setAddToCartEnabled(true);
        }
      } catch (error) {
        this.hide();
      }
    }

    async loadConfig() {
      const url = `${this.configUrl}/products/${this.sku}.json`;
      
      try {
        const response = await fetch(url);
        
        if (!response.ok) {
          if (response.status === 404) {
            return null;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const rawConfig = await response.json();
        
        // Transform JSON structure to match expected format
        this.config = this.transformConfig(rawConfig);
        
        return this.config;
      } catch (error) {
        return null;
      }
    }

    transformConfig(rawConfig) {
      // Transform the JSON structure to match expected format
      // Input format: { zones: [{ code, label: {en, fr}, materials: [...] }] }
      // Output format: { zones: [{ id, name_en, name_fr, options: [...] }] }
      
      if (!rawConfig || !rawConfig.zones) {
        return rawConfig;
      }

      const transformedZones = rawConfig.zones.map(zone => {
        // Transform zone
        const transformedZone = {
          id: zone.code || zone.id, // Use code as id if id doesn't exist
          name_en: zone.label?.en || zone.name_en || zone.code || '',
          name_fr: zone.label?.fr || zone.name_fr || zone.code || '',
          options: []
        };

        // Transform materials to options.
        // PROPRE SOLUTION 1 :
        // Si le JSON du configurateur fournit un addon_variant_id pour un matériau,
        // on le propage jusque dans les options. Cela permet de lier directement
        // chaque finition à une variante add-on Shopify, et donc d'utiliser le prix TTC
        // de cette variante (via ConfiguratorAddonPrices) plutôt qu'un price_cents "maison".
        if (zone.materials && Array.isArray(zone.materials)) {
          transformedZone.options = zone.materials.map(material => ({
            id: material.sku || material.id,
            sku: material.sku,
            name_en: material.name?.en || material.name_en || '',
            name_fr: material.name?.fr || material.name_fr || '',
            image: material.image_url || material.image || '',
            price_cents: material.price_cents || 0,
            // Nouveau champ : id de variante add-on Shopify si fourni par le JSON
            addon_variant_id: material.addon_variant_id || material.addonVariantId || null,
            category: material.category?.en || material.category || '',
            category_name: material.category?.en || material.category || '',
            category_fr: material.category?.fr || material.category || '',
            subcategory: material.sub_category?.en || material.subcategory || '',
            subcategory_name: material.sub_category?.en || material.subcategory || '',
            subcategory_fr: material.sub_category?.fr || material.subcategory || ''
          }));
        } else if (zone.options && Array.isArray(zone.options)) {
          // Already in correct format, just ensure structure
          transformedZone.options = zone.options.map(option => ({
            id: option.sku || option.id,
            sku: option.sku || option.id,
            name_en: option.name?.en || option.name_en || '',
            name_fr: option.name?.fr || option.name_fr || '',
            image: option.image_url || option.image || '',
            price_cents: option.price_cents || 0,
            // Même logique si le JSON est déjà au format options[]
            addon_variant_id: option.addon_variant_id || option.addonVariantId || null,
            category: option.category?.en || option.category || '',
            category_name: option.category?.en || option.category || '',
            category_fr: option.category?.fr || option.category || '',
            subcategory: option.sub_category?.en || option.subcategory || '',
            subcategory_name: option.sub_category?.en || option.subcategory || '',
            subcategory_fr: option.sub_category?.fr || option.subcategory || ''
          }));
        }

        return transformedZone;
      });

      return {
        ...rawConfig,
        zones: transformedZones
      };
    }

    async loadPriceMap() {
      const url = `${this.configUrl}/price_addons.json`;
      
      try {
        const response = await fetch(url);
        
        if (!response.ok) {
          if (response.status === 404) {
            this.priceMap = new Map();
            return null;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        
        // Convert array to Map for O(1) lookup
        this.priceMap = new Map();
        
        if (Array.isArray(data)) {
          data.forEach(item => {
            if (item.price_cents && item.variant_id) {
              const priceCents = parseInt(item.price_cents);
              const variantId = parseInt(item.variant_id);
              if (!isNaN(priceCents) && !isNaN(variantId) && variantId > 0) {
                this.priceMap.set(priceCents, variantId);
              }
            }
          });
        } else if (typeof data === 'object') {
          // Handle object format { "price_cents": variant_id }
          Object.entries(data).forEach(([price, variantId]) => {
            const priceCents = parseInt(price);
            const variantIdNum = parseInt(variantId);
            if (!isNaN(priceCents) && !isNaN(variantIdNum) && variantIdNum > 0) {
              this.priceMap.set(priceCents, variantIdNum);
            }
          });
        }

        return this.priceMap;
      } catch (error) {
        this.priceMap = new Map();
        return null;
      }
    }

    /**
     * Load addon variant prices in current currency from theme (script tag).
     * Ensures configurator total matches cart total in any currency.
     */
    loadAddonVariantPrices() {
      const scriptEl = document.getElementById('ConfiguratorAddonPrices-' + this.sectionId);
      if (!scriptEl || !scriptEl.textContent) {
        if (this.priceDebug) {
          this._priceLog('AddonPrices', 'No addon prices script (ConfiguratorAddonPrices-' + this.sectionId + '); will use eurToLocalRate if set');
        }
        return;
      }
      try {
        const rawText = scriptEl.textContent.trim();
        const data = JSON.parse(rawText);
        this.addonVariantPrices = data && typeof data === 'object' ? data : {};
        if (this.priceDebug) {
          this._priceLog('AddonPrices', 'Loaded addon variant prices (current currency)', {
            variantCount: Object.keys(this.addonVariantPrices).length,
            sample: Object.entries(this.addonVariantPrices).slice(0, 3)
          });
        }
      } catch (e) {
        this.addonVariantPrices = {};
      }
    }

    setupVariantListener() {
      this.variantChangeHandler = async (e) => {
        if (e.detail && e.detail.sectionId === this.sectionId) {
          const newVariant = e.detail.variant;
          const newSku = newVariant?.sku;

          this.currentVariant = newVariant;
          this.variantPriceFromPicker = true;

          // If SKU changed, reload config with new SKU
          if (newSku && newSku !== this.sku) {
            this.sku = newSku;
            this.rootEl.setAttribute('data-product-sku', newSku);
            
            // Reset selections
            this.selectedMaterials = {};
            
            // Reload config with new SKU
            await this.loadConfig();
            
            if (!this.config) {
              this.hide();
              return;
            }
            
            // Reinitialize zone manager with new config
            this.zoneManager = new ZoneManager(this.config.zones || [], this.locale);
            
            // Re-render UI with new config (this will clear and replace existing content)
            this.uiRenderer.render(this.config.zones || []);
            
            // Reset validation
            const hasConfigurableZones = this.config.zones && 
              this.config.zones.some(zone => zone.options && zone.options.length > 0);
            if (hasConfigurableZones) {
              this.validateSelections();
            } else {
              // Enable add to cart if no configurable zones
              this.uiRenderer.setAddToCartEnabled(true);
            }
          }
          
          // Always recalculate total with new variant price (even if SKU didn't change)
          this.recalculateTotal();
        }
      };

      document.addEventListener('variant:change', this.variantChangeHandler);
    }

    /**
     * Price debug logs (when data-price-debug="true" or window.OnoConfigurator.debugPrice).
     */
    _priceLog(step, message, data) {
      if (this.priceDebug && console && typeof console.log === 'function') {
        if (data !== undefined) {
          console.log('[Configurator Price]', step, message, data);
        } else {
          console.log('[Configurator Price]', step, message);
        }
      }
    }

    queryProductElement(selector, context = null) {
      // First try in the specific section context
      const sectionContext = context || document;
      let element = sectionContext.querySelector(`#product-form-${this.sectionId} ${selector}`);
      
      if (element) return element;
      
      // Try with section-specific selector
      element = sectionContext.querySelector(`[data-section-id="${this.sectionId}"] ${selector}`);
      if (element) return element;
      
      // Try in rootEl context (the configurator container)
      if (this.rootEl) {
        element = this.rootEl.closest('section')?.querySelector(selector);
        if (element) return element;
      }
      
      // Last resort: try global but verify it belongs to this product
      const globalElement = document.querySelector(selector);
      if (globalElement) {
        // Verify it's in the same section or product context
        const form = globalElement.closest('form[id^="product-form-"]');
        if (form && form.id === `product-form-${this.sectionId}`) {
          return globalElement;
        }
        // Check if it's in a section with matching product ID
        const section = globalElement.closest(`[data-section-id="${this.sectionId}"], section[id*="${this.sectionId}"]`);
        if (section) {
          return globalElement;
        }
      }
      
      return null;
    }

    updateCurrentVariant() {
      this._priceLog('1.Variant', 'updateCurrentVariant() — finding variant and base price');

      // Try multiple selectors for variant input (scoped to this product)
      let form = document.querySelector(`#product-form-${this.sectionId}`);
      let variantInput = null;
      if (form) {
        variantInput = form.querySelector('input[name="id"]');
      }
      if (!variantInput) {
        variantInput = this.queryProductElement('input[name="id"]');
      }
      // Fallback: section identified by data-section or id (theme: section id="MainProduct-{{ section.id }}" data-section="{{ section.id }}")
      if (!variantInput && this.sectionId) {
        const sectionByData = document.querySelector(
          `section[data-section="${this.sectionId}"], section#MainProduct-${CSS.escape(this.sectionId)}, [data-section="${this.sectionId}"]`
        );
        if (sectionByData) {
          const idInput = sectionByData.querySelector('form input[name="id"]');
          if (idInput) {
            variantInput = idInput;
            form = idInput.closest('form');
          }
        }
      }
      // Fallback: same section as configurator (rootEl's closest section)
      if (!variantInput && this.rootEl) {
        const section = this.rootEl.closest('section');
        if (section) {
          const sectionForm = section.querySelector('form input[name="id"]');
          if (sectionForm) {
            variantInput = sectionForm;
            form = sectionForm.closest('form');
          }
        }
      }
      // Last resort: any add-to-cart form on page (single product page)
      if (!variantInput) {
        const addToCartForm = document.querySelector('form[data-type="add-to-cart-form"]');
        if (addToCartForm) {
          variantInput = addToCartForm.querySelector('input[name="id"]');
          if (variantInput) form = addToCartForm;
        }
      }
      // Very broad fallback: any input[name="id"] (e.g. single-variant product, form id differs from sectionId)
      if (!variantInput) {
        const allIdInputs = Array.from(document.querySelectorAll('input[name="id"]')).filter(function (el) { return el.value && !el.disabled; });
        if (allIdInputs.length === 1) {
          variantInput = allIdInputs[0];
          form = variantInput.closest('form');
        } else if (allIdInputs.length > 1 && this.rootEl) {
          // Prefer the one in the same section as the configurator
          const section = this.rootEl.closest('section');
          const inSection = section ? allIdInputs.find(function (el) { return section.contains(el); }) : null;
          if (inSection) {
            variantInput = inSection;
            form = variantInput.closest('form');
          } else {
            // Prefer one inside a form that looks like main product form (has add button / quantity)
            const inAddToCart = allIdInputs.find(function (el) {
              const f = el.closest('form');
              return f && (f.querySelector('[name="add"]') || f.getAttribute('data-type') === 'add-to-cart-form');
            });
            if (inAddToCart) {
              variantInput = inAddToCart;
              form = variantInput.closest('form');
            } else {
              variantInput = allIdInputs[0];
              form = variantInput.closest('form');
            }
          }
        }
      }
      let variantId = null;
      if (variantInput && variantInput.value) {
        variantId = parseInt(variantInput.value, 10);
        if (!isNaN(variantId) && variantId > 0) this._cachedVariantId = variantId;
      }
      this._priceLog('1.Variant', 'Variant ID from form input[name="id"]', { variantId, formFound: !!form, variantInputFound: !!variantInput });

      // Try to get variant from variant picker (scoped to this section)
      let variantSelects = document.querySelector(`variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`);
      
      // If not found, try to find in the same section context
      if (!variantSelects && this.rootEl) {
        const section = this.rootEl.closest('section');
        if (section) {
          const found = section.querySelector('variant-radios, variant-selects');
          // Use if it's in the same section, or if it has no section attribute (likely the right one for single-product pages)
          if (found && (!found.dataset.section || found.dataset.section === this.sectionId)) {
            variantSelects = found;
          }
        }
      }
      
      // Last fallback: if we still don't have it and there's only one variant picker on the page, use it
      // This is safe because on a product page, there should only be one variant picker
      if (!variantSelects) {
        const allVariantSelects = document.querySelectorAll('variant-radios, variant-selects');
        if (allVariantSelects.length === 1) {
          variantSelects = allVariantSelects[0];
        }
      }
      
      this.variantPriceFromPicker = false;
      if (variantSelects) {
        if (variantSelects.currentVariant) {
          this.currentVariant = variantSelects.currentVariant;
          this.variantPriceFromPicker = true;
          this._priceLog('1.Variant', 'Source: variant-radios/variant-selects .currentVariant', { variantId: this.currentVariant.id, priceCents: this.currentVariant.price });
          return;
        }
        if (typeof variantSelects.getVariantData === 'function') {
          const variantData = variantSelects.getVariantData();
          if (variantData && variantData.length > 0) {
            if (variantId) {
              this.currentVariant = variantData.find(v => v.id === variantId);
            } else {
              this.currentVariant = variantData.find(v => v.available) || variantData[0];
            }
            if (this.currentVariant) {
              this.variantPriceFromPicker = true;
              this._priceLog('1.Variant', 'Source: variant picker getVariantData()', { variantId: this.currentVariant.id, priceCents: this.currentVariant.price });
              return;
            }
          }
        }
      }
      
      // Fallback: get from product data script (ProductJSON-{id}) - MUST match this productId
      const productDataScript = document.querySelector(`#ProductJSON-${this.productId}`) ||
                               document.querySelector(`script[type="application/json"][data-product-id="${this.productId}"]`);
      
      // NEVER use a generic ProductJSON selector without verifying productId
      // This prevents cross-contamination between products
      
      // Products without 3D model have no ProductJSON in this theme; use form + DOM for variant.
      // effectiveVariantId: form input (same section) > cached from DOM > Liquid (data-default-variant-id) when form is in another section.
      const effectiveVariantId = variantId || this._cachedVariantId || this._defaultVariantIdFromLiquid;
      if (!productDataScript && effectiveVariantId) {
        this._priceLog('1.Variant', 'No ProductJSON; using variant id + DOM price', {
          variantId: effectiveVariantId,
          source: variantId ? 'form' : (this._cachedVariantId ? 'cache' : 'liquid')
        });
        this._setCurrentVariantFromFormAndDom(effectiveVariantId);
        return;
      }
      
      if (productDataScript) {
        try {
          const scriptContent = productDataScript.textContent.trim();
          
          if (scriptContent) {
            const productData = JSON.parse(scriptContent);
            
            // If script contains an array (e.g. 3D media, not product data), use form + DOM fallback for variant
            const formVariantInputForFallback = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
            const variantIdFromFormFallback = formVariantInputForFallback ? parseInt(formVariantInputForFallback.value, 10) : null;
            if (Array.isArray(productData) && variantIdFromFormFallback) {
              this._priceLog('1.Variant', 'ProductJSON is array (e.g. 3D media), using form + DOM price');
              this._setCurrentVariantFromFormAndDom(variantIdFromFormFallback);
              return;
            }

            // Check if productData is null or invalid
            if (!productData) {
              // Try to get variant ID from form as fallback (scoped to this section)
              const formVariantInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
              const variantIdFromForm = formVariantInput ? parseInt(formVariantInput.value) : null;
              
              if (variantIdFromForm) {
                // Try to get price from price element (scoped to this section)
                const priceElement = document.querySelector(`#price-${this.sectionId}`);
                let price = 0;
                if (priceElement) {
                  const priceText = priceElement.textContent || priceElement.innerText;
                  
                  // Better regex to match price with European or US format
                  // Match: numbers with optional thousands separator and decimal part
                  const priceMatch = priceText.match(/[\d.,]+/);
                  if (priceMatch) {
                    const matchedValue = priceMatch[0];
                    let domPriceValue;
                    
                    // Handle European format (comma as decimal) or US format (dot as decimal)
                    if (matchedValue.includes(',') && matchedValue.includes('.')) {
                      // Both separators: assume dot is thousands, comma is decimal (e.g., "4.068,00")
                      domPriceValue = parseFloat(matchedValue.replace(/\./g, '').replace(',', '.'));
                    } else if (matchedValue.includes(',')) {
                      // Only comma: check if it's decimal (2 digits after) or thousands
                      const parts = matchedValue.split(',');
                      if (parts.length === 2 && parts[1].length <= 2) {
                        // Decimal separator (e.g., "4068,00" or "4.068,00")
                        domPriceValue = parseFloat(matchedValue.replace(/\./g, '').replace(',', '.'));
                      } else {
                        // Thousands separator (e.g., "4,068")
                        domPriceValue = parseFloat(matchedValue.replace(/,/g, ''));
                      }
                    } else if (matchedValue.includes('.')) {
                      // Only dot: check if it's decimal (2 digits after) or thousands
                      const parts = matchedValue.split('.');
                      if (parts.length === 2 && parts[1].length <= 2) {
                        // Decimal separator (e.g., "4068.00")
                        domPriceValue = parseFloat(matchedValue);
                      } else {
                        // Thousands separator (e.g., "4.068")
                        domPriceValue = parseFloat(matchedValue.replace(/\./g, ''));
                      }
                    } else {
                      // No separator, just digits
                      domPriceValue = parseFloat(matchedValue);
                    }
                    
                    // The price from DOM is always in main currency units (e.g., 4068.00 for €4.068,00)
                    // We need to convert to cents by multiplying by 100
                    // But first, check if the extraction preserved the decimal part correctly
                    const hasDecimalInOriginal = (matchedValue.includes(',') && matchedValue.split(',')[1] && matchedValue.split(',')[1].length <= 2) ||
                                                  (matchedValue.includes('.') && matchedValue.split('.').length === 2 && matchedValue.split('.')[1].length <= 2);
                    
                    // If domPriceValue is very large (> 10000) and has no decimal part in original,
                    // it might be that the extraction lost the decimal separator
                    // In that case, we need to check if dividing by 100 gives a reasonable price
                    let priceInCents;
                    if (domPriceValue > 10000 && !hasDecimalInOriginal) {
                      // Large value without decimal in original - might be extraction issue
                      // Check if dividing by 100 gives a reasonable price (between 10 and 100000)
                      const possibleMainCurrency = domPriceValue / 100;
                      if (possibleMainCurrency >= 10 && possibleMainCurrency <= 100000) {
                        // This looks like it was already in cents but extraction lost the format
                        priceInCents = Math.round(domPriceValue);
                      } else {
                        // Unlikely to be in cents, treat as main currency
                        priceInCents = Math.round(domPriceValue * 100);
                      }
                    } else {
                      // Normal case: price is in main currency, convert to cents
                      priceInCents = Math.round(domPriceValue * 100);
                    }
                    
                    price = priceInCents;
                  }
                }
                
              this.currentVariant = {
                id: variantIdFromForm,
                price: price,
                sku: this.sku || '',
                available: true,
                title: ''
              };
              this._priceLog('1.Variant', 'Source: form + DOM (productData null)', { variantId: variantIdFromForm, priceCents: price });
                return;
              }
              return;
            }
            
            // Handle both array of variants and object with variants property
            let variants = null;
            if (Array.isArray(productData)) {
              variants = productData;
            } else if (productData && productData.variants && Array.isArray(productData.variants)) {
              variants = productData.variants;
            } else if (productData && productData.variant) {
              this.currentVariant = productData.variant;
              this._priceLog('1.Variant', 'Source: ProductJSON productData.variant', { variantId: this.currentVariant.id, priceCents: this.currentVariant.price });
              return;
            }

            if (productData && productData.selected_or_first_available_variant) {
              this.currentVariant = productData.selected_or_first_available_variant;
              if (this.currentVariant.price && this.currentVariant.price < 10000) {
                this.currentVariant.price = Math.round(this.currentVariant.price * 100);
              }
              this._priceLog('1.Variant', 'Source: ProductJSON selected_or_first_available_variant', { variantId: this.currentVariant.id, priceCents: this.currentVariant.price });
              return;
            }
            
            if (variants && Array.isArray(variants) && variants.length > 0) {
              if (variantId) {
                this.currentVariant = variants.find(v => v.id === variantId);
              } else {
                this.currentVariant = variants.find(v => v.available) || variants[0];
              }
              if (this.currentVariant) {
                this._priceLog('1.Variant', 'Source: ProductJSON variants[]', { variantId: this.currentVariant.id, priceCents: this.currentVariant.price });
                // CRITICAL: Verify this variant belongs to this product
                // Only check if both productData.id and this.productId exist and are valid
                // Some product data structures might not have an id field, so we allow that
                if (productData.id && this.productId) {
                  const productDataId = String(productData.id);
                  const expectedProductId = String(this.productId);
                  if (productDataId !== expectedProductId) {
                    // Product ID mismatch - this is from a different product!
                    console.warn(`[Configurator] Variant found but product ID mismatch: expected ${expectedProductId}, got ${productDataId}`);
                    this.currentVariant = null;
                    return;
                  }
                }
                // If productData.id doesn't exist, we trust that the ProductJSON-{productId} selector
                // already ensured we're getting the right product's data
                return;
              }
            }
            
            // If product has no variants array but has a price, try to get variant ID from form
            // For products with only default variant, the variant ID should be in the form input (scoped to this section)
            const formVariantInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
            const variantIdFromForm = formVariantInput ? parseInt(formVariantInput.value) : null;
            
            if (variantIdFromForm && productData && productData.price) {
              // For products without variants, we need to determine if productData.price is in cents or main currency
              // Compare with DOM price to determine the format
              let priceInCents = productData.price;
              
              const priceElement = document.querySelector(`#price-${this.sectionId}`);
              if (priceElement) {
                const priceText = priceElement.textContent || priceElement.innerText;
                // Extract numeric value from price (handle formats like "€891,30" or "891.30" or "891,30")
                // Try to match numbers with optional decimal separator
                const priceMatch = priceText.match(/[\d,]+\.?\d*/);
                if (priceMatch) {
                  let domPriceValue;
                  const matchedValue = priceMatch[0];
                  
                  // Handle European format (comma as decimal separator) or US format (dot as decimal separator)
                  if (matchedValue.includes(',') && matchedValue.includes('.')) {
                    // Both separators: assume comma is thousands, dot is decimal (e.g., "1.234,56")
                    domPriceValue = parseFloat(matchedValue.replace(/\./g, '').replace(',', '.'));
                  } else if (matchedValue.includes(',')) {
                    // Only comma: could be decimal (European) or thousands separator
                    // If there are 3 digits after comma, it's likely thousands separator
                    const parts = matchedValue.split(',');
                    if (parts.length === 2 && parts[1].length <= 2) {
                      // Decimal separator (e.g., "891,30")
                      domPriceValue = parseFloat(matchedValue.replace(',', '.'));
                    } else {
                      // Thousands separator (e.g., "1,234")
                      domPriceValue = parseFloat(matchedValue.replace(/,/g, ''));
                    }
                  } else {
                    // Only dot or no separator
                    domPriceValue = parseFloat(matchedValue);
                  }
                  
                  const domPriceInCents = Math.round(domPriceValue * 100);
                  
                  // If productData.price is very close to domPriceInCents, it's already in cents
                  if (Math.abs(productData.price - domPriceInCents) < 10) {
                    priceInCents = productData.price;
                  } else if (Math.abs(productData.price - domPriceValue) < 1) {
                    // If productData.price is very close to domPriceValue, it's in main currency
                    priceInCents = Math.round(productData.price * 100);
                  } else {
                    // Default: assume it's already in cents (Shopify standard for variant prices)
                    priceInCents = productData.price;
                  }
                } else {
                  // Couldn't parse DOM price, assume productData.price is already in cents
                  priceInCents = productData.price;
                }
              } else {
                // No DOM price to compare, assume productData.price is already in cents (Shopify standard)
                priceInCents = productData.price;
              }
              
              this.currentVariant = {
                id: variantIdFromForm,
                price: priceInCents,
                sku: this.sku || (productData.sku || ''),
                available: productData.available !== false,
                title: productData.title || ''
              };
              this._priceLog('1.Variant', 'Source: ProductJSON form id + productData.price (no variants array)', { variantId: variantIdFromForm, priceCents: priceInCents });
              return;
            }

            // Last fallback: if we have variant ID from form but no productData.price, try to get price from DOM
            if (variantIdFromForm) {
              const priceElement = document.querySelector(`#price-${this.sectionId}`);
              let price = 0;
              if (priceElement) {
                const priceText = priceElement.textContent || priceElement.innerText;
                const priceMatch = priceText.match(/[\d,]+\.?\d*/);
                if (priceMatch) {
                  // Convert to cents (assuming price is in main currency unit)
                  price = Math.round(parseFloat(priceMatch[0].replace(/,/g, '')) * 100);
                }
              }
              
              if (price > 0) {
                this.currentVariant = {
                  id: variantIdFromForm,
                  price: price,
                  sku: this.sku || '',
                  available: true,
                  title: ''
                };
                this._priceLog('1.Variant', 'Source: ProductJSON form id + DOM price (no productData.price)', { variantId: variantIdFromForm, priceCents: price });
                return;
              }
            }
          }
        } catch (e) {
          // Silent error handling
        }
      }

      // Last resort: if we have SKU but no variant, try to get price from product element
      if (!this.currentVariant && this.sku) {
        const priceElement = document.querySelector(`#price-${this.sectionId}, [id^="price-"]`);
        if (priceElement) {
          const priceText = priceElement.textContent || priceElement.innerText;
          // Try to extract price (this is a fallback, not ideal)
          const priceMatch = priceText.match(/[\d,]+\.?\d*/);
          // This is a very rough fallback - we don't have the actual variant ID
        }
      }
      
      if (!this.currentVariant && variantId) {
        this._priceLog('1.Variant', 'Last resort: form variant ID + DOM price');
        this._setCurrentVariantFromFormAndDom(variantId);
      }

      if (!this.currentVariant) {
        this._priceLog('1.Variant', 'No variant found');
        this.currentVariant = null;
      }
    }

    _setCurrentVariantFromFormAndDom(variantId) {
      const priceElement = document.querySelector(`#price-${this.sectionId}`);
      let price = 0;
      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.innerText;
        const priceMatch = priceText.match(/[\d.,]+/);
        if (priceMatch) {
          const domPriceValue = parsePriceString(priceMatch[0]);
          if (!isNaN(domPriceValue)) {
            price = Math.round(domPriceValue * 100);
          }
        }
      }
      if (variantId) {
        this.currentVariant = {
          id: variantId,
          price: price > 0 ? price : 0,
          sku: this.sku || '',
          available: true,
          title: ''
        };
        this._priceLog('1.Variant', '_setCurrentVariantFromFormAndDom result', { variantId, priceCents: price > 0 ? price : 0, domPriceElement: !!priceElement });
      }
    }

    /**
     * Fallback: fetch variant id from current product page URL (/products/handle.json).
     * Shopify /cart/add requires variant id (not product id); this gets the first variant when Liquid didn't provide it.
     * @returns {Promise<number|null>} variant id or null
     */
    async _fetchVariantIdFromProductPageUrl() {
      const match = typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.match(/^\/products\/([^/]+)/);
      if (!match || !match[1]) return null;
      try {
        const url = window.location.pathname + '.json';
        const res = await fetch(url);
        if (!res.ok) return null;
        const data = await res.json();
        const product = data && data.product;
        if (!product) return null;
        const variant = (product.selected_or_first_available_variant || (product.variants && product.variants[0]));
        const id = variant && variant.id;
        return id ? parseInt(id, 10) : null;
      } catch (_) {
        return null;
      }
    }

    selectMaterial(zoneId, material) {
      this.selectedMaterials[zoneId] = material;
      this.uiRenderer.updateSelection(zoneId, material);
      this.recalculateTotal();
      this.validateSelections();
    }

    _getPriceCentsFromDom() {
      const priceElement = document.querySelector(`#price-${this.sectionId}`);
      if (!priceElement) {
        this._priceLog('2.DOM', '_getPriceCentsFromDom: no element #price-' + this.sectionId);
        return 0;
      }
      const priceText = (priceElement.textContent || priceElement.innerText || '').trim();
      const priceMatch = priceText.match(/[\d.,]+/);
      if (!priceMatch) {
        this._priceLog('2.DOM', '_getPriceCentsFromDom: no number in text', { priceText: priceText.slice(0, 80) });
        return 0;
      }
      const matchedValue = priceMatch[0];
      const domPriceValue = parsePriceString(matchedValue);
      if (isNaN(domPriceValue)) {
        this._priceLog('2.DOM', '_getPriceCentsFromDom: NaN', { matchedValue });
        return 0;
      }
      const cents = Math.round(domPriceValue * 100);
      this._priceLog('2.DOM', '_getPriceCentsFromDom', { rawText: priceText.slice(0, 60), matchedValue, parsedUnit: domPriceValue, resultCents: cents });
      return cents;
    }

    recalculateTotal() {
      this._priceLog('3.Recalc', 'recalculateTotal() start');

      if (!this.currentVariant) {
        this._priceLog('3.Recalc', 'No currentVariant, calling updateCurrentVariant()');
        this.updateCurrentVariant();
      }

      if (!this.currentVariant) {
        this._priceLog('3.Recalc', 'Still no variant, deferring (setTimeout) → will retry or use recalculateTotalFromDOM');
        setTimeout(() => {
          this.updateCurrentVariant();
          if (this.currentVariant) {
            this.recalculateTotal();
          } else {
            this.recalculateTotalFromDOM();
          }
        }, 100);
        return;
      }

      if (!this.variantPriceFromPicker) {
        const domPriceCents = this._getPriceCentsFromDom();
        if (domPriceCents > 0) {
          this._priceLog('3.Recalc', 'Refreshing base price from DOM (currency/sync)', { previousCents: this.currentVariant.price, newCents: domPriceCents });
          this.currentVariant.price = domPriceCents;
        }
      } else if (this.priceDebug) {
        this._priceLog('3.Recalc', 'Keeping variant price from picker (DOM not used on variant switch)', { priceCents: this.currentVariant.price });
      }

      if (!this.currentVariant.price) {
        this._priceLog('3.Recalc', 'Variant has no price → recalculateTotalFromDOM()');
        this.recalculateTotalFromDOM();
        return;
      }

      const total = this.priceCalculator.calculate(
        this.currentVariant,
        this.selectedMaterials
      );
      this._priceLog('3.Recalc', 'PriceCalculator result', total);

      this.uiRenderer.updateTotal(total);
    }

    recalculateTotalFromDOM() {
      this._priceLog('3.Recalc', 'recalculateTotalFromDOM() — fallback when no variant or no variant price');

      const priceElement = document.querySelector(`#price-${this.sectionId}`);
      let basePrice = 0;

      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.innerText;
        const priceMatch = priceText.match(/[\d.,]+/);
        if (priceMatch) {
          const domPriceValue = parsePriceString(priceMatch[0]);
          if (!isNaN(domPriceValue)) {
            basePrice = Math.round(domPriceValue * 100);
          }
        }
      }

      const addonPrices = this.addonVariantPrices || {};
      const eurToLocal = this.eurToLocalRate != null ? this.eurToLocalRate : null;
      let totalDeltas = 0;
      const deltasBreakdown = [];
      Object.entries(this.selectedMaterials).forEach(([zoneId, material]) => {
        if (material && material.price_cents) {
          const eurCents = parseInt(material.price_cents);
          const variantId = this.priceMap && this.priceMap.get(eurCents);
          let localCents;
          let source;
          if (variantId != null && (addonPrices[variantId] != null || addonPrices[String(variantId)] != null)) {
            const fromAddon = addonPrices[variantId] != null ? addonPrices[variantId] : addonPrices[String(variantId)];
            localCents = Math.round(fromAddon);
            source = 'addon_variant';
          } else {
            localCents = eurToLocal != null ? Math.round(eurCents * eurToLocal) : eurCents;
            source = eurToLocal != null ? 'eur_rate' : 'eur_raw';
          }
          totalDeltas += localCents;
          deltasBreakdown.push({ zone: zoneId, price_cents_local: localCents, source: source });
        }
      });

      const total = basePrice + totalDeltas;
      this._priceLog('3.Recalc', 'recalculateTotalFromDOM result', {
        baseFromDOMCents: basePrice,
        optionsCents: totalDeltas,
        optionsBreakdown: deltasBreakdown,
        totalCents: total
      });

      this.uiRenderer.updateTotal({
        base: basePrice,
        deltas: totalDeltas,
        total: total
      });
    }

    validateSelections() {
      const allZonesSelected = this.config.zones.every(zone => 
        this.selectedMaterials[zone.id]
      );

      const allPricesAvailable = Object.values(this.selectedMaterials).every(material => {
        if (!material || !material.price_cents) return true; // No price = free
        return this.priceMap.has(parseInt(material.price_cents));
      });

      const isValid = allZonesSelected && allPricesAvailable;

      // Highlight missing zones
      this.config.zones.forEach(zone => {
        const zoneEl = this.rootEl.querySelector(`[data-zone-id="${zone.id}"]`);
        if (zoneEl) {
          if (!this.selectedMaterials[zone.id]) {
            zoneEl.classList.add('configurator-zone--missing');
          } else {
            zoneEl.classList.remove('configurator-zone--missing');
          }
        }
      });

      // Always enable quote button - no validation required
      this.uiRenderer.setAddToCartEnabled(true);

      // Hide validation message - no validation required since button is always enabled
      this.uiRenderer.hideValidationMessage();
    }

    pushDataLayerEvent(eventName, extraData = {}) {
      if (!eventName) return;
      if (!Array.isArray(window.dataLayer)) {
        window.dataLayer = [];
      }
      window.dataLayer.push({
        event: eventName,
        product_sku: this.sku || '',
        product_id: this.productId || '',
        ...extraData
      });
    }

    requestQuote(quantity = 1) {
      // Open quote modal - no validation required, user can submit even without finishes
      this.pushDataLayerEvent('quote_modal_open', {
        quantity: Number(quantity) || 1
      });
      this.uiRenderer.showQuoteModal(quantity);
    }

    async buildQuoteMessage(formData, quantity) {
      // Ensure variant is up to date
      if (!this.currentVariant) {
        this.updateCurrentVariant();
      }
      
      // Build email message with all information
      // 1) Try to use the product title passed via data attribute on the configurator root
      let productTitle = (this.rootEl && this.rootEl.dataset && this.rootEl.dataset.productTitle) 
        ? this.rootEl.dataset.productTitle 
        : '';

      // 2) Fallback to DOM selectors if needed (scoped to this product section)
      if (!productTitle) {
        // Try to find title in the same section as this configurator
        let titleEl = null;
        if (this.rootEl) {
          const section = this.rootEl.closest('section');
          if (section) {
            titleEl = section.querySelector('.product__title h1, .product__title h2, h1.product__title, .product__title');
          }
        }
        // If still not found, try with section ID
        if (!titleEl) {
          const section = document.querySelector(`section[data-section-id="${this.sectionId}"], section[id*="${this.sectionId}"]`);
          if (section) {
            titleEl = section.querySelector('.product__title h1, .product__title h2, h1.product__title, .product__title');
          }
        }
        if (titleEl && titleEl.textContent) {
          productTitle = titleEl.textContent.trim();
        }
      }

      // 3) Final fallback
      if (!productTitle) {
        productTitle = 'Product';
      }
      const productSku = this.sku || 'N/A';
      
      // Get variant information
      let variantInfo = '';
      if (this.currentVariant) {
        variantInfo = `- Variant ID: ${this.currentVariant.id}\n`;
        if (this.currentVariant.title && this.currentVariant.title !== 'Default Title') {
          variantInfo += `- Variant: ${this.currentVariant.title}\n`;
        }
      }

      // Get base price (MUST be from this product, not another tab/product)
      let basePrice = 'Price calculation pending';
      if (this.currentVariant && this.currentVariant.price) {
        // Verify the variant belongs to this product
        if (this.currentVariant.id && this.productId) {
          // Price is from currentVariant which was already validated to belong to this product
          basePrice = this.formatPrice(this.currentVariant.price);
        } else {
          basePrice = this.formatPrice(this.currentVariant.price);
        }
      } else {
        // Try to get price from DOM (scoped to this product section)
        let priceEl = null;
        if (this.rootEl) {
          const section = this.rootEl.closest('section');
          if (section) {
            priceEl = section.querySelector('#price-' + this.sectionId + ' .price-item--regular, .price--on-sale .price-item--regular, .price__regular .price-item--regular');
          }
        }
        // If still not found, try with section ID
        if (!priceEl) {
          const section = document.querySelector(`section[data-section-id="${this.sectionId}"], section[id*="${this.sectionId}"]`);
          if (section) {
            priceEl = section.querySelector('#price-' + this.sectionId + ' .price-item--regular, .price--on-sale .price-item--regular, .price__regular .price-item--regular');
          }
        }
        // Last resort: try section-specific price element
        if (!priceEl) {
          priceEl = document.querySelector(`#price-${this.sectionId} .price-item--regular`);
        }
        if (priceEl) {
          basePrice = priceEl.textContent.trim();
        }
      }

      // Build finishes section
      let finishesText = '';
      const hasFinishes = this.config.zones && this.config.zones.length > 0 && 
        this.config.zones.some(zone => this.selectedMaterials[zone.id]);
      
      if (hasFinishes) {
        const finishes = [];
        this.config.zones.forEach(zone => {
          const material = this.selectedMaterials[zone.id];
          if (material) {
            const zoneName = this.locale === 'fr' ? zone.name_fr : zone.name_en;
            const materialName = this.locale === 'fr' ? material.name_fr : material.name_en;
            finishes.push(`- ${zoneName}: ${materialName}`);
          }
        });
        finishesText = finishes.join('\n');
      } else {
        finishesText = '- No finishes selected';
      }

      // Calculate total price if possible
      let totalPrice = 'Price calculation pending';
      if (this.priceCalculator) {
        try {
          // Try with currentVariant first
          if (this.currentVariant && this.currentVariant.price) {
            const total = this.priceCalculator.calculate(this.currentVariant, this.selectedMaterials);
            if (total && total.total && total.total > 0) {
              totalPrice = this.formatPrice(total.total);
            }
          } else {
            // Fallback: calculate from base price and selected materials
            let basePriceCents = 0;
            
            // Try to get base price from variant
            if (this.currentVariant && this.currentVariant.price) {
              basePriceCents = parseInt(this.currentVariant.price);
            } else {
              // Try to get from DOM
              const priceElement = document.querySelector(`#price-${this.sectionId}`);
              if (priceElement) {
                const priceText = priceElement.textContent || priceElement.innerText;
                const priceMatch = priceText.match(/[\d.,]+/);
                if (priceMatch) {
                  const matchedValue = priceMatch[0];
                  let domPriceValue;
                  
                  if (matchedValue.includes(',') && matchedValue.includes('.')) {
                    domPriceValue = parseFloat(matchedValue.replace(/\./g, '').replace(',', '.'));
                  } else if (matchedValue.includes(',')) {
                    const parts = matchedValue.split(',');
                    if (parts.length === 2 && parts[1].length <= 2) {
                      domPriceValue = parseFloat(matchedValue.replace(/\./g, '').replace(',', '.'));
                    } else {
                      domPriceValue = parseFloat(matchedValue.replace(/,/g, ''));
                    }
                  } else if (matchedValue.includes('.')) {
                    const parts = matchedValue.split('.');
                    if (parts.length === 2 && parts[1].length <= 2) {
                      domPriceValue = parseFloat(matchedValue);
                    } else {
                      domPriceValue = parseFloat(matchedValue.replace(/\./g, ''));
                    }
                  } else {
                    domPriceValue = parseFloat(matchedValue);
                  }
                  
                  basePriceCents = Math.round(domPriceValue * 100);
                }
              }
            }
            
            // Calculate deltas from selected materials
            let totalDeltas = 0;
            Object.values(this.selectedMaterials).forEach(material => {
              if (material && material.price_cents) {
                totalDeltas += parseInt(material.price_cents);
              }
            });
            
            const totalCents = basePriceCents + totalDeltas;
            if (totalCents > 0) {
              totalPrice = this.formatPrice(totalCents);
            }
          }
        } catch (e) {
          // Swallow errors in price calculation to avoid breaking the quote flow
          // Total price will remain as "Price calculation pending" in that case
        }
      }

      // Build message body
      let customerInfo = `Customer Information:
- Name: ${formData.firstName} ${formData.lastName}
- Email: ${formData.email}
- Phone: ${formData.phone}`;

      if (formData.company) {
        customerInfo += `\n- Company: ${formData.company}`;
      }

      customerInfo += `\n- Destination Country: ${formData.country}`;

      if (formData.city) {
        customerInfo += `\n- City: ${formData.city}`;
      }

      if (formData.note) {
        customerInfo += `\n\nAdditional Note:\n${formData.note}`;
      }

      return `Quote Request from Product Configurator

${customerInfo}

Product Information:
- Product: ${productTitle}
- SKU: ${productSku}
${variantInfo}- Quantity: ${quantity}
- Base Price: ${basePrice}

Selected Finishes:
${finishesText}

Total Price: ${totalPrice}`;
    }

    formatPrice(cents) {
      if (typeof cents !== 'number') {
        cents = parseInt(cents) || 0;
      }
      var format = (window.theme && window.theme.moneyFormat) || (window.Shopify && window.Shopify.money_format) || '{{amount}}';
      var result;
      if (typeof formatMoney === 'function') {
        result = formatMoney(cents, format);
      } else if (window.Shopify && window.Shopify.formatMoney) {
        result = window.Shopify.formatMoney(cents, format);
      } else {
        var amount = (cents / 100).toFixed(2);
        result = '€' + amount.replace('.', ',');
      }
      this._priceLog('5.Format', 'formatPrice (devise / currency)', { cents, moneyFormat: format, formatted: result });
      return result;
    }

    async addToCart(quantity = 1) {
      // Validate before adding
      const allZonesSelected = this.config.zones.every(zone => 
        this.selectedMaterials[zone.id]
      );
      if (!allZonesSelected) {
        const missingZones = this.config.zones.filter(zone => !this.selectedMaterials[zone.id]);
        this.uiRenderer.showValidationMessage('incomplete', missingZones);
        // Scroll to first missing zone
        const firstMissingZone = this.rootEl.querySelector(`[data-zone-id="${missingZones[0].id}"]`);
        if (firstMissingZone) {
          firstMissingZone.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a pulse animation
          firstMissingZone.classList.add('configurator-zone--pulse');
          setTimeout(() => {
            firstMissingZone.classList.remove('configurator-zone--pulse');
          }, 2000);
        }
        return;
      }

      // Ensure variant is up to date before adding to cart
      if (!this.currentVariant) {
        this.updateCurrentVariant();
      }

      // Use Liquid variant id if we have it but updateCurrentVariant didn't set currentVariant (e.g. form in another section)
      if (!this.currentVariant && this._defaultVariantIdFromLiquid) {
        this._setCurrentVariantFromFormAndDom(this._defaultVariantIdFromLiquid);
      }

      // Last resort: fetch variant from product page URL
      if (!this.currentVariant) {
        const variantIdFromUrl = await this._fetchVariantIdFromProductPageUrl();
        if (variantIdFromUrl) {
          this._setCurrentVariantFromFormAndDom(variantIdFromUrl);
        }
      }

      if (!this.currentVariant) {
        // Diagnostic logs for "product variant not found"
        const form = document.querySelector(`#product-form-${this.sectionId}`);
        const variantInput = form ? form.querySelector('input[name="id"]') : null;
        const productDataScript = document.querySelector(`#ProductJSON-${this.productId}`);
        console.warn('[Configurator] Add to cart failed: no variant. Diagnostics:', {
          productId: this.productId,
          sectionId: this.sectionId,
          defaultVariantIdFromLiquid: this._defaultVariantIdFromLiquid,
          formFound: !!form,
          formId: form ? form.id : null,
          variantInputFound: !!variantInput,
          variantInputValue: variantInput ? variantInput.value : null,
          productJsonScriptFound: !!productDataScript,
          productJsonContentType: (function () {
            if (!productDataScript || !productDataScript.textContent) return 'n/a';
            try {
              const p = JSON.parse(productDataScript.textContent.trim() || 'null');
              return Array.isArray(p) ? 'array (e.g. 3D media)' : (p && typeof p === 'object' ? 'object' : 'other');
            } catch (_) { return 'parse error'; }
          })(),
          hint: 'Shopify /cart/add requires variant id (not product id). Check defaultVariantIdFromLiquid or form input name="id".'
        });
        this.uiRenderer.showError('Unable to add to cart: product variant not found. Please refresh the page.');
        return;
      }

      // Build items array
      const items = [];

      // Main product item
      if (this.currentVariant) {
        const mainProperties = {};
        let totalPriceSupplement = 0;
        
        this.config.zones.forEach(zone => {
          const material = this.selectedMaterials[zone.id];
          if (material) {
            const zoneName = this.locale === 'fr' ? zone.name_fr : zone.name_en;
            const materialName = this.locale === 'fr' ? material.name_fr : material.name_en;
            // Keep properties visible in checkout (without _ prefix) but format without "Zone: " and "Material: "
            mainProperties[zoneName] = materialName;
            
            // Add price supplement to properties for checkout script (hidden with _ prefix)
            if (material.price_cents) {
              const priceCents = parseInt(material.price_cents);
              totalPriceSupplement += priceCents;
              mainProperties[`_PriceSupplement_${zoneName}`] = priceCents;
            }
          }
        });
        
        // Add total price supplement as a property (for checkout script)
        if (totalPriceSupplement > 0) {
          mainProperties['_TotalPriceSupplement'] = totalPriceSupplement;
        }

        const mainVariantId = parseInt(this.currentVariant.id);
        if (isNaN(mainVariantId) || mainVariantId <= 0) {
          throw new Error(`Invalid main variant ID: ${this.currentVariant.id}`);
        }
        
        items.push({
          id: mainVariantId,
          quantity: quantity,
          properties: mainProperties
        });
      }

      // Add-on items
      let addOnCount = 0;
      this.config.zones.forEach(zone => {
        const material = this.selectedMaterials[zone.id];
        if (material && material.price_cents) {
          const priceCents = parseInt(material.price_cents);
          const variantId = this.priceMap.get(priceCents);

          if (!variantId) {
            return;
          }

          // Declare zoneName and materialName before using them
          const zoneName = this.locale === 'fr' ? zone.name_fr : zone.name_en;
          const materialName = this.locale === 'fr' ? material.name_fr : material.name_en;

          // Ensure variantId is a valid number
          const addOnVariantId = parseInt(variantId);
          if (isNaN(addOnVariantId) || addOnVariantId <= 0) {
            throw new Error(`Invalid add-on variant ID: ${variantId} for price ${priceCents} cents`);
          }

          items.push({
            id: addOnVariantId,
            quantity: quantity,
            properties: {
              '_Zone': zoneName,
              '_Material': materialName,
              '_Material SKU': material.sku || '',
              '_Parent SKU': this.sku
            }
          });
          addOnCount++;
        }
      });

      // POST to cart
      try {
        const config = this.getFetchConfig();
        
        // Ensure all IDs are numbers and validate
        const validatedItems = items.map((item, index) => {
          const variantId = parseInt(item.id);
          if (isNaN(variantId) || variantId <= 0) {
            throw new Error(`Invalid variant ID: ${item.id} (at index ${index})`);
          }
          
          const validated = {
            id: variantId,
            quantity: parseInt(item.quantity) || 1,
            properties: item.properties || {}
          };
          
          return validated;
        });
        
        // Validate that we have at least one item
        if (validatedItems.length === 0) {
          throw new Error('No items to add to cart');
        }
        
        // Final validation: ensure all IDs are numbers (not strings)
        const finalItems = validatedItems.map(item => ({
          id: Number(item.id), // Ensure it's a number, not a string
          quantity: Number(item.quantity) || 1,
          properties: item.properties || {}
        }));
        
        const body = JSON.stringify({ items: finalItems });
        
        // Ensure we're using the .js endpoint for JSON response
        // Shopify routes.cart_add_url might be '/cart/add' without .js
        let cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
        if (!cartAddUrl.endsWith('.js')) {
          cartAddUrl = cartAddUrl + '.js';
        }
        
        // Send all items to cart at once
        const response = await fetch(cartAddUrl, {
          ...config,
          body: body
        });

        // Parse response - Shopify cart/add.js should return JSON
        let data;
        const contentType = response.headers.get('content-type') || '';
        
        // Clone response to read it multiple times if needed
        const responseClone = response.clone();
        
        try {
          // Try to parse as JSON directly (like product-form.js does)
          data = await response.json();
        } catch (jsonError) {
          // If JSON parsing fails, read as text to see what we got
          const text = await responseClone.text();
          
          // Check if it's HTML
          if (text.includes('<!DOCTYPE') || text.includes('<html') || text.trim().startsWith('<')) {
            // Try to extract error message from HTML
            const errorMatch = text.match(/<[^>]*class="[^"]*error[^"]*"[^>]*>([^<]+)</i) || 
                                text.match(/error[^<]*>([^<]+)</i) ||
                                text.match(/(422|400|500)[^<]*>([^<]+)</i);
            const errorMessage = errorMatch ? errorMatch[1].trim() : `Server returned ${response.status} with HTML response. This usually means the request format is incorrect.`;
            throw new Error(errorMessage);
          }
          
          // If it's not HTML, try to parse as JSON anyway (might be valid JSON with wrong content-type)
          try {
            data = JSON.parse(text);
          } catch (parseError) {
            throw new Error(`Failed to parse cart response. Expected JSON but got: ${text.substring(0, 100)}...`);
          }
        }

        if (data.status) {
          // Error
          // Try to identify which variant is problematic by checking the error message
          if (data.description && data.description.includes('variant')) {
            // Check if the error message contains a specific variant ID
            const variantIdMatch = data.description.match(/variant[:\s]+(\d+)/i) || 
                                   data.description.match(/(\d{10,})/); // Match long numbers (likely variant IDs)
            if (variantIdMatch) {
              const problematicId = variantIdMatch[1];
              const problematicItem = finalItems.find(item => String(item.id) === problematicId);
              if (problematicItem) {
                const itemIndex = finalItems.indexOf(problematicItem);
                // If it's an add-on, show the price mapping
                if (itemIndex > 0) {
                  // Silent error handling
                }
              }
            }
          }
          
          // If we can't identify which variant, test each add-on separately
          if (finalItems.length > 1) {
            const invalidAddOns = [];
            
            for (let i = 1; i < finalItems.length; i++) {
              const addOnItem = finalItems[i];
              const priceCents = Array.from(this.priceMap.entries()).find(([price, variantId]) => 
                variantId === addOnItem.id
              )?.[0];
              
              try {
                // First, try without properties to see if the variant itself is valid
                const testItemWithoutProps = {
                  id: addOnItem.id,
                  quantity: addOnItem.quantity
                };
                const testBodyNoProps = JSON.stringify({ items: [testItemWithoutProps] });
                
                const testResponseNoProps = await fetch(cartAddUrl, {
                  ...config,
                  body: testBodyNoProps
                });
                const testDataNoProps = await testResponseNoProps.json();
                
                if (testDataNoProps.status) {
                  // The variant itself doesn't exist or is not available
                  invalidAddOns.push({
                    index: i,
                    variantId: addOnItem.id,
                    priceCents: priceCents,
                    material: addOnItem.properties?._Material,
                    zone: addOnItem.properties?._Zone,
                    reason: 'Variant does not exist, is not available, or the product is unpublished in sales channels. Please ensure the "CONFIGURATOR - PRICE ADDONS" product is published in the sales channels.'
                  });
                } else {
                  // If it works without properties, try with properties
                  const testBodyWithProps = JSON.stringify({ items: [addOnItem] });
                  const testResponseWithProps = await fetch(cartAddUrl, {
                    ...config,
                    body: testBodyWithProps
                  });
                  const testDataWithProps = await testResponseWithProps.json();
                  
                  if (testDataWithProps.status) {
                    invalidAddOns.push({
                      index: i,
                      variantId: addOnItem.id,
                      priceCents: priceCents,
                      material: addOnItem.properties?._Material,
                      zone: addOnItem.properties?._Zone,
                      reason: 'Variant exists but fails with properties'
                    });
                  }
                }
              } catch (testError) {
                // Silent error handling
              }
            }
            
            // If we found invalid add-ons, show a helpful error message
            if (invalidAddOns.length > 0) {
              const errorDetails = invalidAddOns.map(addOn => {
                if (addOn.reason === 'Variant exists but fails with properties') {
                  return `Variant ID ${addOn.variantId} exists but cannot be added with properties. This might be a Shopify configuration issue.`;
                } else {
                  return `Variant ID ${addOn.variantId} for price ${addOn.priceCents} cents (${addOn.material}) ${addOn.reason || 'does not exist or is not available in Shopify'}. IMPORTANT: The "CONFIGURATOR - PRICE ADDONS" product must be published in the sales channels for variants to be added to cart. Please publish the product in Shopify admin.`;
                }
              }).join(' ');
              
              this.uiRenderer.showError(`Cannot add to cart: ${errorDetails}`);
              return;
            }
          }
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                source: "product-configurator",
                errors: data.description,
                message: data.message,
              },
            })
          );
          this.uiRenderer.showError(data.description || data.message);
          return;
        }

        // Success - dispatch cart:change event
        document.dispatchEvent(
          new CustomEvent("cart:change", {
            detail: data,
            bubbles: true,
          })
        );

        // Open cart drawer
        const cart = document.querySelector('cart-drawer');
        if (cart) {
          // cart-drawer.js expects parsedState.sections, but /cart/add.js only returns items
          // We need to fetch the sections separately
          if (cart.getSectionsToRender) {
            const sectionsToRender = cart.getSectionsToRender();
            
            // Fetch each section separately (Shopify doesn't support multiple section_id in one request)
            const sectionPromises = sectionsToRender.map(section => {
              const sectionId = (section.section != null && section.section !== '') ? section.section : section.id;
              if (!sectionId) return Promise.resolve({ id: section.id, section: section.section, html: null });
              return fetch(`${window.routes.cart_url}?section_id=${sectionId}`)
                .then(response => {
                  if (!response.ok) {
                    throw new Error(`Failed to fetch section ${section.id}: ${response.status}`);
                  }
                  return response.text();
                })
                .then(html => {
                  const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
                  // Try to find the section element using the selector from getSectionsToRender
                  let sectionElement = null;
                  
                  // First try the selector if provided
                  if (section.selector) {
                    sectionElement = parsedHTML.querySelector(section.selector);
                  }
                  
                  // If not found, try by ID
                  if (!sectionElement) {
                    sectionElement = parsedHTML.getElementById(section.id);
                  }
                  
                  // If still not found, try to find any element with the id in attributes
                  if (!sectionElement) {
                    sectionElement = parsedHTML.querySelector(`[id*="${section.id}"]`);
                  }
                  
                  // If still not found, try to find the section wrapper
                  if (!sectionElement) {
                    sectionElement = parsedHTML.querySelector(`.shopify-section[id*="${section.id}"]`);
                  }
                  
                  // Extract innerHTML if we found an element, otherwise use the full HTML
                  let sectionHTML = html;
                  if (sectionElement) {
                    // Use getSectionInnerHTML method if available, otherwise use outerHTML
                    if (cart.getSectionInnerHTML && typeof cart.getSectionInnerHTML === 'function') {
                      try {
                        sectionHTML = cart.getSectionInnerHTML(sectionElement.outerHTML, section.selector);
                        // Wrap in the section structure if needed
                        sectionHTML = `<div id="${section.id}">${sectionHTML}</div>`;
                      } catch (e) {
                        sectionHTML = sectionElement.outerHTML;
                      }
                    } else {
                      sectionHTML = sectionElement.outerHTML;
                    }
                  }
                  
                  return {
                    id: section.id, // DOM ID (CartDrawer, cart-icon-bubble)
                    section: section.section, // Section name (cart-drawer, cart-icon-bubble)
                    html: sectionHTML
                  };
                })
                .catch(error => {
                  return { id: section.id, section: section.section, html: null };
                });
            });
            
            Promise.all(sectionPromises)
              .then(sectionResults => {
                // Update drawer and icon from fetched HTML (theme may use id="CartDrawer" so renderContents can fail)
                const drawerResult = sectionResults.find(r => (r.id === 'cart-drawer' || r.id === 'CartDrawer') && r.html);
                const iconResult = sectionResults.find(r => r.id === 'cart-icon-bubble' && r.html);
                if (drawerResult && drawerResult.html) {
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(drawerResult.html, 'text/html');
                  const drawerContent = doc.querySelector('.drawer__inner');
                  const currentDrawer = cart.querySelector('.drawer__inner');
                  if (drawerContent && currentDrawer) {
                    currentDrawer.innerHTML = drawerContent.innerHTML;
                    currentDrawer.classList.contains('is-empty') && currentDrawer.classList.remove('is-empty');
                  }
                  cart.classList.contains('is-empty') && cart.classList.remove('is-empty');
                }
                if (iconResult && iconResult.html) {
                  const iconEl = document.getElementById('cart-icon-bubble');
                  if (iconEl) {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(iconResult.html, 'text/html');
                    const bubble = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.cart-count-bubble');
                    if (bubble) iconEl.innerHTML = bubble.innerHTML;
                  }
                }
                if (drawerResult && drawerResult.html) {
                  cart.open();
                } else {
                  fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
                    .then(response => response.text())
                    .then(html => {
                      const parser = new DOMParser();
                      const doc = parser.parseFromString(html, 'text/html');
                      const drawerContent = doc.querySelector('.drawer__inner');
                      const currentDrawer = cart.querySelector('.drawer__inner');
                      if (drawerContent && currentDrawer) {
                        currentDrawer.innerHTML = drawerContent.innerHTML;
                      }
                      cart.open();
                    })
                    .catch(() => {
                      cart.open();
                      document.dispatchEvent(new CustomEvent('cart:refresh'));
                    });
                }
              })
              .catch(error => {
                // Fallback: open drawer and trigger refresh
                cart.open();
                document.dispatchEvent(new CustomEvent('cart:refresh'));
              });
          } else {
            // Fallback if getSectionsToRender doesn't exist
            cart.open();
          }
        } else {
          window.location = window.routes.cart_url;
        }

      } catch (error) {
        this.uiRenderer.showError('An error occurred. Please try again.');
      }
    }

    getFetchConfig() {
      // Use same config as product-form.js
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && cart.getSectionsToRender) {
        // Add sections for cart drawer refresh
        const sections = cart.getSectionsToRender().map(s => s.id).join(',');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
      }

      return config;
    }

    hide() {
      this.rootEl.style.display = 'none';
    }

    destroy() {
      if (this.variantChangeHandler) {
        document.removeEventListener('variant:change', this.variantChangeHandler);
      }
    }
  }

  /**
   * ZoneManager - Manages zone selections
   */
  class ZoneManager {
    constructor(zones, locale) {
      this.zones = zones;
      this.locale = locale;
    }

    getZoneName(zone) {
      return this.locale === 'fr' ? zone.name_fr : zone.name_en;
    }

    getCategoryName(category) {
      // Handle both object with name property and object with name_en/name_fr
      if (typeof category === 'string') {
        return category;
      }
      if (category.name_fr || category.name_en) {
        return this.locale === 'fr' ? category.name_fr : category.name_en;
      }
      return category.name || 'Other';
    }

    getSubcategoryName(subcategory) {
      // Handle both object with name property and object with name_en/name_fr
      if (typeof subcategory === 'string') {
        return subcategory;
      }
      if (subcategory.name_fr || subcategory.name_en) {
        return this.locale === 'fr' ? subcategory.name_fr : subcategory.name_en;
      }
      return subcategory.name || 'Other';
    }

    groupOptionsByCategory(zone) {
      const groups = {};
      
      zone.options.forEach(option => {
        const catKey = option.category || 'other';
        const subcatKey = option.subcategory || 'other';
        
        if (!groups[catKey]) {
          // Store both localized names
          groups[catKey] = {
            name: option.category_name || catKey,
            name_en: option.category_name || catKey,
            name_fr: option.category_fr || option.category_name || catKey,
            subcategories: {}
          };
        }
        
        if (!groups[catKey].subcategories[subcatKey]) {
          // Store both localized names
          groups[catKey].subcategories[subcatKey] = {
            name: option.subcategory_name || subcatKey,
            name_en: option.subcategory_name || subcatKey,
            name_fr: option.subcategory_fr || option.subcategory_name || subcatKey,
            options: []
          };
        }
        
        groups[catKey].subcategories[subcatKey].options.push(option);
      });

      return groups;
    }
  }

  /**
   * PriceCalculator - Calculates total price
   */
  class PriceCalculator {
    constructor(priceMap, configurator) {
      this.priceMap = priceMap;
      this.configurator = configurator;
    }

    calculate(variant, selectedMaterials) {
      if (!variant || !variant.price) {
        if (this.configurator && this.configurator._priceLog) {
          this.configurator._priceLog('4.Calc', 'PriceCalculator.calculate: no variant or price', { hasVariant: !!variant, price: variant && variant.price });
        }
        return { base: 0, deltas: 0, total: 0 };
      }

      const basePrice = parseInt(variant.price);
      const addonPrices = this.configurator && this.configurator.addonVariantPrices ? this.configurator.addonVariantPrices : {};
      const eurToLocal = this.configurator && this.configurator.eurToLocalRate != null ? this.configurator.eurToLocalRate : null;
      let totalDeltas = 0;
      const deltasBreakdown = [];
      const verboseMaterials = [];

      Object.entries(selectedMaterials || {}).forEach(([zoneId, material]) => {
        if (material && material.price_cents) {
          const eurCents = parseInt(material.price_cents);
          // PROPRE SOLUTION 1 : priorité au lien direct vers une variante add-on Shopify
          const directAddonVariantIdRaw = material.addon_variant_id || material.addonVariantId || null;
          const directAddonVariantId = directAddonVariantIdRaw ? parseInt(directAddonVariantIdRaw, 10) : null;
          const mappedVariantId = this.priceMap.get(eurCents);

          let localCents;
          let source;
          let fromAddon = null;
          let effectiveVariantId = null;

          // 1) Si le JSON fournit addon_variant_id ET qu'on a un prix dans AddonPrices → c'est la source la plus fiable (TTC Shopify)
          if (directAddonVariantId && (addonPrices[directAddonVariantId] != null || addonPrices[String(directAddonVariantId)] != null)) {
            effectiveVariantId = directAddonVariantId;
            fromAddon = addonPrices[directAddonVariantId] != null ? addonPrices[directAddonVariantId] : addonPrices[String(directAddonVariantId)];
            if (typeof fromAddon === 'number' && fromAddon >= 0) {
              localCents = Math.round(fromAddon);
              source = 'addon_variant_direct';
            }
          }

          // 2) Sinon : on tente la correspondance par price_cents → variantId (grille price_addons.json)
          if (!localCents && mappedVariantId != null) {
            effectiveVariantId = mappedVariantId;
            fromAddon = addonPrices[mappedVariantId] != null ? addonPrices[mappedVariantId] : addonPrices[String(mappedVariantId)];
            if (typeof fromAddon === 'number' && fromAddon >= 0) {
              localCents = Math.round(fromAddon);
              source = 'addon_variant';
            }
          }

          // 3) Dernier recours : calcul maison à partir de eurCents (potentiellement HT ou sans la logique complète de TVA)
          if (!localCents) {
            localCents = eurToLocal != null ? Math.round(eurCents * eurToLocal) : eurCents;
            source = eurToLocal != null ? 'eur_rate' : 'eur_raw';
          }

          totalDeltas += localCents;
          deltasBreakdown.push({
            zone: zoneId,
            material: material.name_en || material.sku,
            variantId: effectiveVariantId,
            price_cents_local: localCents,
            source: source
          });
          verboseMaterials.push({
            zone: zoneId,
            material_name: material.name_en || material.name_fr || material.sku,
            price_cents_from_json: eurCents,
            addon_variant_id_from_json: directAddonVariantId ?? null,
            variant_id_from_priceMap: mappedVariantId ?? null,
            addon_price_cents: fromAddon != null ? (typeof fromAddon === 'number' ? Math.round(fromAddon) : fromAddon) : null,
            localCents_used: localCents,
            source
          });
        }
      });

      const total = basePrice + totalDeltas;

      if (this.configurator && this.configurator._priceLog) {
        this.configurator._priceLog('4.Calc', 'PriceCalculator.calculate', {
          formula: 'base + options (addon prices = cart; else eur rate/raw) = total',
          baseCents: basePrice,
          optionsCents: totalDeltas,
          optionsBreakdown: deltasBreakdown,
          totalCents: total,
          addonPricesUsed: Object.keys(addonPrices).length > 0
        });
      }

      return {
        base: basePrice,
        deltas: totalDeltas,
        total: total
      };
    }

    formatPrice(cents) {
      if (typeof formatMoney === 'function') {
        return formatMoney(cents);
      }
      // Fallback
      return (cents / 100).toFixed(2);
    }
  }

  /**
   * UIRenderer - Handles all UI rendering
   */
  class UIRenderer {
    constructor(rootEl, locale, configurator) {
      this.rootEl = rootEl;
      this.locale = locale;
      this.configurator = configurator;
      this.modalOpen = false;
      this.currentZone = null;
      this.lastUnitTotal = 0; // per-unit total in cents, for quantity * price display
    }

    render(zones) {
      // Clear existing content to prevent duplication
      this.rootEl.innerHTML = '';
      
      // Create main container
      const container = document.createElement('div');
      container.className = 'configurator-container';

      // Check if there are zones with options
      const hasConfigurableZones = zones && zones.some(zone => zone.options && zone.options.length > 0);

      // Render zone cards only if there are configurable zones
      if (hasConfigurableZones) {
        const zonesContainer = document.createElement('div');
        zonesContainer.className = 'configurator-zones';
        zones.forEach((zone) => {
          if (zone.options && zone.options.length > 0) {
            zonesContainer.appendChild(this.renderZoneCard(zone));
          }
        });
        container.appendChild(zonesContainer);

        // Render modal only if there are configurable zones
        const modal = this.renderModal();
        container.appendChild(modal);
      }

      // Always render sticky recap (with or without zones)
      const stickyRecap = this.renderStickyRecap();
      container.appendChild(stickyRecap);

      this.rootEl.appendChild(container);
    }

    renderZoneCard(zone) {
      
      // Validate zone structure
      if (!zone || !zone.id) {
        return document.createElement('div'); // Return empty div as fallback
      }
      
      const card = document.createElement('div');
      card.className = 'configurator-zone';
      card.dataset.zoneId = zone.id;

      const zoneName = this.configurator.zoneManager.getZoneName(zone);
      
      const header = document.createElement('div');
      header.className = 'configurator-zone__header';
      header.innerHTML = `<h3 class="configurator-zone__title">${this.escapeHtml(zoneName)}</h3>`;
      card.appendChild(header);

      const optionsContainer = document.createElement('div');
      optionsContainer.className = 'configurator-zone__options';
      
      // Validate and handle options
      const options = Array.isArray(zone.options) ? zone.options : [];
      
      if (options.length === 0) {
        const noOptionsMsg = document.createElement('div');
        noOptionsMsg.className = 'configurator-zone__no-options';
        noOptionsMsg.textContent = 'No options available';
        optionsContainer.appendChild(noOptionsMsg);
      } else {
        // Reorder options to put selected material first
        const selectedMaterial = this.configurator.selectedMaterials[zone.id];
        let orderedOptions = [...options];
        
        if (selectedMaterial) {
          // Find the selected material in the options
          const selectedIndex = orderedOptions.findIndex(opt => opt && opt.id === selectedMaterial.id);
          if (selectedIndex > 0) {
            // Move selected material to the beginning
            const [selected] = orderedOptions.splice(selectedIndex, 1);
            orderedOptions.unshift(selected);
          }
        }
        
        // Show first 4 options (with selected first if it exists)
        const visibleOptions = orderedOptions.slice(0, 4);
        visibleOptions.forEach((option, index) => {
          if (!option || !option.id) {
            return;
          }
          optionsContainer.appendChild(this.renderOption(option, zone.id, index < 4));
        });

        // "View all" button if more than 4 options
        if (options.length > 4) {
          const viewAllBtn = document.createElement('button');
          viewAllBtn.className = 'configurator-zone__view-all';
          viewAllBtn.textContent = this.getTranslation('zones.view_all');
          viewAllBtn.addEventListener('click', () => this.openModal(zone));
          optionsContainer.appendChild(viewAllBtn);
        }
      }

      card.appendChild(optionsContainer);

      return card;
    }

    renderOption(option, zoneId, isVisible) {
      const optionEl = document.createElement('button');
      optionEl.className = 'configurator-option';
      optionEl.dataset.zoneId = zoneId;
      optionEl.dataset.optionId = option.id;
      optionEl.type = 'button';
      
      if (!isVisible) {
        optionEl.classList.add('configurator-option--hidden');
      }

      const materialName = this.locale === 'fr' ? option.name_fr : option.name_en;

      // Add zoom button for visible options (first 4)
      const zoomButton = isVisible && option.image ? `
        <button class="configurator-option__zoom" aria-label="Zoom image" type="button">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M19 19l-4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      ` : '';

      optionEl.innerHTML = `
        <div class="configurator-option__image">
          ${option.image ? `<img src="${this.escapeHtml(option.image)}" alt="${this.escapeHtml(materialName)}" loading="lazy">` : ''}
          ${zoomButton}
        </div>
        <div class="configurator-option__name">${this.escapeHtml(materialName)}</div>
      `;

      // Add zoom functionality for visible options
      if (isVisible && option.image) {
        const zoomBtn = optionEl.querySelector('.configurator-option__zoom');
        if (zoomBtn) {
          zoomBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.zoomImage(option.image);
          });
        }
      }

      optionEl.addEventListener('click', () => {
        this.configurator.selectMaterial(zoneId, option);
      });

      return optionEl;
    }

    renderModal() {
      const modal = document.createElement('div');
      modal.className = 'configurator-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', this.getTranslation('modal.title'));

      const overlay = document.createElement('div');
      overlay.className = 'configurator-modal__overlay';
      overlay.addEventListener('click', () => this.closeModal());
      modal.appendChild(overlay);

      const sidebar = document.createElement('div');
      sidebar.className = 'configurator-modal__sidebar';

      const header = document.createElement('div');
      header.className = 'configurator-modal__header';
      header.innerHTML = `
        <h2 class="configurator-modal__title"></h2>
        <button class="configurator-modal__close" aria-label="${this.getTranslation('modal.close')}">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      `;
      header.querySelector('.configurator-modal__close').addEventListener('click', () => this.closeModal());
      sidebar.appendChild(header);

      const searchContainer = document.createElement('div');
      searchContainer.className = 'configurator-modal__search';
      searchContainer.innerHTML = `
        <input type="text" 
               class="configurator-modal__search-input" 
               placeholder="${this.getTranslation('modal.search_placeholder')}"
               aria-label="${this.getTranslation('modal.search_placeholder')}">
      `;
      sidebar.appendChild(searchContainer);

      const content = document.createElement('div');
      content.className = 'configurator-modal__content';
      sidebar.appendChild(content);

      modal.appendChild(sidebar);
      modal.dataset.modal = 'true';

      // ESC key handler (will be attached per modal instance)
      // Handled in openModal/closeModal

      return modal;
    }

    openModal(zone) {
      this.currentZone = zone;
      const modal = this.rootEl.querySelector('.configurator-modal');
      const content = modal.querySelector('.configurator-modal__content');
      const title = modal.querySelector('.configurator-modal__title');
      const searchInput = modal.querySelector('.configurator-modal__search-input');

      const zoneName = this.configurator.zoneManager.getZoneName(zone);
      title.textContent = zoneName;

      // Render grouped options
      const groups = this.configurator.zoneManager.groupOptionsByCategory(zone);
      content.innerHTML = '';

      Object.entries(groups).forEach(([catKey, category]) => {
        const categoryEl = document.createElement('div');
        categoryEl.className = 'configurator-modal__category';

        const categoryName = this.configurator.zoneManager.getCategoryName(category);
        const categoryHeader = document.createElement('h3');
        categoryHeader.className = 'configurator-modal__category-title';
        categoryHeader.innerHTML = `
          <span>${this.escapeHtml(categoryName)}</span>
          <span class="configurator-modal__category-toggle">▼</span>
        `;
        categoryHeader.addEventListener('click', () => {
          categoryEl.classList.toggle('configurator-modal__category--collapsed');
        });
        categoryEl.appendChild(categoryHeader);

        Object.entries(category.subcategories).forEach(([subcatKey, subcategory]) => {
          const subcategoryEl = document.createElement('div');
          subcategoryEl.className = 'configurator-modal__subcategory';

          const subcategoryName = this.configurator.zoneManager.getSubcategoryName(subcategory);
          if (subcategoryName && subcategoryName !== 'other') {
            const subcategoryHeader = document.createElement('h4');
            subcategoryHeader.className = 'configurator-modal__subcategory-title';
            subcategoryHeader.innerHTML = `
              <span>${this.escapeHtml(subcategoryName)}</span>
              <span class="configurator-modal__subcategory-toggle">▼</span>
            `;
            subcategoryHeader.addEventListener('click', () => {
              subcategoryEl.classList.toggle('configurator-modal__subcategory--collapsed');
            });
            subcategoryEl.appendChild(subcategoryHeader);
          }

          const optionsGrid = document.createElement('div');
          optionsGrid.className = 'configurator-modal__options-grid';

          subcategory.options.forEach(option => {
            const optionCard = this.renderModalOption(option, zone.id);
            optionsGrid.appendChild(optionCard);
          });

          subcategoryEl.appendChild(optionsGrid);
          categoryEl.appendChild(subcategoryEl);
        });

        content.appendChild(categoryEl);
      });

      // Search functionality
      searchInput.value = '';
      const searchHandler = (e) => {
        this.filterModalOptions(e.target.value);
      };
      searchInput.removeEventListener('input', searchHandler); // Remove old listener if any
      searchInput.addEventListener('input', searchHandler);

      modal.classList.add('configurator-modal--open');
      this.modalOpen = true;
      document.body.classList.add('configurator-modal-open');

      // ESC key handler for this modal instance
      const escHandler = (e) => {
        if (e.key === 'Escape' && this.modalOpen) {
          this.closeModal();
        }
      };
      document.addEventListener('keydown', escHandler);
      modal.dataset.escHandler = 'attached'; // Mark for cleanup

      // Focus trap using theme's trapFocus function
      const sidebar = modal.querySelector('.configurator-modal__sidebar');
      if (typeof trapFocus === 'function' && sidebar) {
        trapFocus(sidebar, searchInput);
      } else {
        // Fallback
        const firstFocusable = modal.querySelector('button, input, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();
      }
    }

    renderModalOption(option, zoneId) {
      const card = document.createElement('button');
      card.className = 'configurator-modal__option';
      card.type = 'button';
      card.dataset.zoneId = zoneId;
      card.dataset.optionId = option.id;
      card.dataset.searchText = ((this.locale === 'fr' ? option.name_fr : option.name_en) || '').toLowerCase();

      const materialName = this.locale === 'fr' ? option.name_fr : option.name_en;

      card.innerHTML = `
        <div class="configurator-modal__option-image">
          ${option.image ? `<img src="${this.escapeHtml(option.image)}" alt="${this.escapeHtml(materialName)}" loading="lazy">` : ''}
          <button class="configurator-modal__option-zoom" aria-label="Zoom image">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M19 19l-4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="configurator-modal__option-name">${this.escapeHtml(materialName)}</div>
      `;

      card.querySelector('.configurator-modal__option-zoom')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.zoomImage(option.image);
      });

      card.addEventListener('click', () => {
        this.configurator.selectMaterial(zoneId, option);
        
        // On mobile, close modal after 1 second
        if (window.innerWidth <= 749) {
          setTimeout(() => {
            this.closeModal();
          }, 1000);
        } else {
          this.closeModal();
        }
      });

      return card;
    }

    filterModalOptions(searchTerm) {
      const term = searchTerm.toLowerCase();
      const options = this.rootEl.querySelectorAll('.configurator-modal__option');

      options.forEach(option => {
        const searchText = option.dataset.searchText || '';
        const matches = searchText.includes(term);
        option.style.display = matches ? '' : 'none';
      });

      // Show/hide categories/subcategories based on visible options
      const categories = this.rootEl.querySelectorAll('.configurator-modal__category');
      categories.forEach(category => {
        const visibleOptions = category.querySelectorAll('.configurator-modal__option:not([style*="display: none"])');
        category.style.display = visibleOptions.length > 0 ? '' : 'none';
      });
    }

    zoomImage(imageUrl) {
      // Simple zoom modal (can be enhanced)
      const zoomModal = document.createElement('div');
      zoomModal.className = 'configurator-zoom-modal';
      zoomModal.innerHTML = `
        <div class="configurator-zoom-modal__overlay"></div>
        <div class="configurator-zoom-modal__content">
          <button class="configurator-zoom-modal__close" aria-label="Close">×</button>
          <img src="${this.escapeHtml(imageUrl)}" alt="Zoomed material">
        </div>
      `;

      // Close on overlay click
      zoomModal.querySelector('.configurator-zoom-modal__overlay')
        .addEventListener('click', () => zoomModal.remove());

      // Close on button click
      zoomModal.querySelector('.configurator-zoom-modal__close')
        .addEventListener('click', (e) => {
          e.stopPropagation();
          zoomModal.remove();
        });

      // Close on ESC key
      const escHandler = (e) => {
        if (e.key === 'Escape') {
          zoomModal.remove();
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);

      document.body.appendChild(zoomModal);
    }

    closeModal() {
      const modal = this.rootEl.querySelector('.configurator-modal');
      if (modal) {
        modal.classList.remove('configurator-modal--open');
        this.modalOpen = false;
        document.body.classList.remove('configurator-modal-open');
        
        // Remove focus trap
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }
        
        // Note: ESC handler cleanup would require storing the handler reference
        // For now, the handler checks this.modalOpen so it won't fire when closed
        
        this.currentZone = null;
      }
    }

    showQuoteModal(quantity) {
      // Create quote modal if it doesn't exist
      let quoteModal = this.rootEl.querySelector('.configurator-quote-modal');
      if (!quoteModal) {
        quoteModal = this.createQuoteModal();
        document.body.appendChild(quoteModal);
      }

      // Reset form
      const form = quoteModal.querySelector('.configurator-quote-modal__form');
      if (form) {
        form.reset();
        // Store quantity in form data attribute
        form.dataset.quantity = quantity || 1;
      }

      // Show modal
      quoteModal.classList.add('configurator-quote-modal--open');
      document.body.classList.add('configurator-quote-modal-open');

      // Focus first input
      const firstInput = quoteModal.querySelector('input[type="text"], input[type="email"]');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }

      // Add focus trap
      if (typeof trapFocus === 'function') {
        trapFocus(quoteModal);
      }
    }

    createQuoteModal() {
      const modal = document.createElement('div');
      modal.className = 'configurator-quote-modal';
      modal.innerHTML = `
        <div class="configurator-quote-modal__overlay"></div>
        <div class="configurator-quote-modal__content">
          <div class="configurator-quote-modal__header">
            <h2 class="configurator-quote-modal__title">${this.getTranslation('quote_modal.title')}</h2>
            <button class="configurator-quote-modal__close" aria-label="${this.getTranslation('modal.close')}">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <form class="configurator-quote-modal__form" method="POST" action="/contact">
            <!-- Hidden field required by Shopify -->
            <input type="hidden" name="form_type" value="contact">
            
            <div class="configurator-quote-modal__fields-grid">
              <div class="configurator-quote-modal__field">
                <label for="quote-first-name">${this.getTranslation('quote_modal.first_name')} <span class="required">*</span></label>
                <input type="text" id="quote-first-name" name="firstName" required aria-required="true">
              </div>
              <div class="configurator-quote-modal__field">
                <label for="quote-last-name">${this.getTranslation('quote_modal.last_name')} <span class="required">*</span></label>
                <input type="text" id="quote-last-name" name="lastName" required aria-required="true">
              </div>
              <div class="configurator-quote-modal__field">
                <label for="quote-email">${this.getTranslation('quote_modal.email')} <span class="required">*</span></label>
                <input type="email" id="quote-email" name="contact[email]" required aria-required="true">
              </div>
              <div class="configurator-quote-modal__field">
                <label for="quote-phone">${this.getTranslation('quote_modal.phone')} <span class="required">*</span></label>
                <input type="tel" id="quote-phone" name="contact[phone]" required aria-required="true">
              </div>
              <div class="configurator-quote-modal__field">
                <label for="quote-company">${this.getTranslation('quote_modal.company')}</label>
                <input type="text" id="quote-company" name="company">
              </div>
              <div class="configurator-quote-modal__field">
                <label for="quote-country">${this.getTranslation('quote_modal.country')} <span class="required">*</span></label>
                <select id="quote-country" name="country" required aria-required="true">
                  <option value="">${this.getTranslation('quote_modal.country')}</option>
                  ${this.getCountriesList()}
                </select>
              </div>
              <div class="configurator-quote-modal__field">
                <label for="quote-city">${this.getTranslation('quote_modal.city')} <span class="required">*</span></label>
                <input type="text" id="quote-city" name="city" required aria-required="true">
              </div>
              <div class="configurator-quote-modal__field configurator-quote-modal__field--full-width">
                <label for="quote-note">${this.getTranslation('quote_modal.note')}</label>
                <textarea id="quote-note" name="note" rows="4" placeholder="${this.getTranslation('quote_modal.note')}"></textarea>
              </div>
            </div>
            <!-- Hidden fields for Shopify contact form -->
            <input type="hidden" name="contact[name]" id="contact-name-hidden">
            <textarea name="contact[body]" id="contact-body-hidden" style="display: none;"></textarea>
            ${this.locale === 'fr' ? '<textarea name="contact[comment]" id="contact-comment-hidden" style="display: none;"></textarea>' : ''}
            <input type="hidden" name="contact[tags]" value="quote_request">
            
            <div class="configurator-quote-modal__actions">
              <button type="button" class="configurator-quote-modal__cancel button button--secondary">
                ${this.getTranslation('quote_modal.cancel')}
              </button>
              <button type="submit" class="configurator-quote-modal__submit button button--primary">
                ${this.getTranslation('quote_modal.submit')}
              </button>
            </div>
            <div class="configurator-quote-modal__message" style="display: none;"></div>
          </form>
        </div>
      `;

      // Close handlers
      const overlay = modal.querySelector('.configurator-quote-modal__overlay');
      const closeBtn = modal.querySelector('.configurator-quote-modal__close');
      const cancelBtn = modal.querySelector('.configurator-quote-modal__cancel');

      const closeModal = () => {
        modal.classList.remove('configurator-quote-modal--open');
        document.body.classList.remove('configurator-quote-modal-open');
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus();
        }
      };

      overlay.addEventListener('click', closeModal);
      closeBtn.addEventListener('click', closeModal);
      cancelBtn.addEventListener('click', closeModal);

      // ESC key handler
      const escHandler = (e) => {
        if (e.key === 'Escape' && modal.classList.contains('configurator-quote-modal--open')) {
          closeModal();
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);

      // Form submission
      const form = modal.querySelector('.configurator-quote-modal__form');
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const submitBtn = form.querySelector('.configurator-quote-modal__submit');
        const messageDiv = form.querySelector('.configurator-quote-modal__message');
        const quantity = parseInt(form.dataset.quantity) || 1;

        // Disable submit button
        submitBtn.disabled = true;
        submitBtn.textContent = this.locale === 'fr' ? 'Envoi...' : 'Sending...';

        // Get form data
        const firstName = form.querySelector('[name="firstName"]').value.trim();
        const lastName = form.querySelector('[name="lastName"]').value.trim();
        const email = form.querySelector('[name="contact[email]"]').value.trim();
        const phone = form.querySelector('[name="contact[phone]"]').value.trim();
        const company = form.querySelector('[name="company"]').value.trim();
        const country = form.querySelector('[name="country"]').value;
        const city = form.querySelector('[name="city"]').value.trim();
        const note = form.querySelector('[name="note"]').value.trim();

        // Validate required fields
        if (!firstName || !lastName || !email || !phone || !country || !city) {
          messageDiv.style.display = 'block';
          messageDiv.className = 'configurator-quote-modal__message configurator-quote-modal__message--error';
          messageDiv.textContent = this.getTranslation('quote_modal.required');
          submitBtn.disabled = false;
          submitBtn.textContent = this.getTranslation('quote_modal.submit');
          return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          messageDiv.style.display = 'block';
          messageDiv.className = 'configurator-quote-modal__message configurator-quote-modal__message--error';
          messageDiv.textContent = this.getTranslation('quote_modal.email_invalid');
          submitBtn.disabled = false;
          submitBtn.textContent = this.getTranslation('quote_modal.submit');
          return;
        }

        // Build message body
        const messageBody = await this.configurator.buildQuoteMessage({
          firstName,
          lastName,
          email,
          phone,
          company,
          country,
          city,
          note
        }, quantity);

        // Fill hidden Shopify contact form fields
        const nameField = form.querySelector('#contact-name-hidden');
        const bodyField = form.querySelector('#contact-body-hidden');
        const commentField = form.querySelector('#contact-comment-hidden');
        
        if (nameField) nameField.value = `${firstName} ${lastName}`;
        
        // For French locale, use contact[comment], otherwise use contact[body]
        // This prevents duplication - Shopify will use the appropriate field based on locale
        if (this.locale === 'fr' && commentField) {
          commentField.value = messageBody;
          // Clear body field to avoid duplication
          if (bodyField) bodyField.value = '';
        } else {
          if (bodyField) bodyField.value = messageBody;
          // Clear comment field to avoid duplication
          if (commentField) commentField.value = '';
        }

        // Submit the form using a hidden iframe to avoid page reload
        const iframe = document.createElement('iframe');
        iframe.name = 'quote-form-iframe';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        
        form.target = 'quote-form-iframe';
        
        // Listen for iframe load to detect success
        let submissionComplete = false;
        const checkSubmission = () => {
          if (submissionComplete) return;
          
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            const iframeContent = iframeDoc.body ? iframeDoc.body.innerHTML : '';
            
            // Check for success indicators
            if (iframeContent.includes('success') || iframeContent.includes('merci') || 
                iframeContent.includes('thank') || iframeContent.includes('envoy') ||
                iframeContent.includes('post_success') || iframeDoc.URL.includes('/contact')) {
              submissionComplete = true;
              if (this.configurator && typeof this.configurator.pushDataLayerEvent === 'function') {
                this.configurator.pushDataLayerEvent('quote_submitted', {
                  quantity: Number(quantity) || 1
                });
              }
              
              // Show success message
              messageDiv.style.display = 'block';
              messageDiv.className = 'configurator-quote-modal__message configurator-quote-modal__message--success';
              messageDiv.innerHTML = `
                <strong>${this.getTranslation('recap.quote_success_title')}</strong><br>
                ${this.getTranslation('recap.quote_success_message')}
              `;
              
              // Remove iframe
              setTimeout(() => {
                document.body.removeChild(iframe);
                closeModal();
              }, 2000);
            } else if (iframeContent.includes('error') || iframeContent.includes('erreur')) {
              submissionComplete = true;
              
              // Show error message
              messageDiv.style.display = 'block';
              messageDiv.className = 'configurator-quote-modal__message configurator-quote-modal__message--error';
              messageDiv.textContent = this.getTranslation('recap.quote_error');
              submitBtn.disabled = false;
              submitBtn.textContent = this.getTranslation('quote_modal.submit');
              
              // Remove iframe
              setTimeout(() => {
                document.body.removeChild(iframe);
              }, 1000);
            }
          } catch (e) {
            // Cross-origin or not loaded yet, wait a bit
            setTimeout(checkSubmission, 500);
          }
        };
        
        // Submit form
        form.submit();
        
        // Check submission status after a delay
        setTimeout(checkSubmission, 1000);
        setTimeout(() => {
          if (!submissionComplete) {
            // Assume success if no error detected after 3 seconds
            submissionComplete = true;
            if (this.configurator && typeof this.configurator.pushDataLayerEvent === 'function') {
              this.configurator.pushDataLayerEvent('quote_submitted', {
                quantity: Number(quantity) || 1
              });
            }
            messageDiv.style.display = 'block';
            messageDiv.className = 'configurator-quote-modal__message configurator-quote-modal__message--success';
            messageDiv.innerHTML = `
              <strong>${this.getTranslation('recap.quote_success_title')}</strong><br>
              ${this.getTranslation('recap.quote_success_message')}
            `;
            setTimeout(() => {
              if (iframe.parentNode) {
                document.body.removeChild(iframe);
              }
              closeModal();
            }, 2000);
          }
        }, 3000);
      });

      return modal;
    }

    getCountriesList() {
      // List of countries (common ones first, then alphabetical)
      const countries = [
        'France', 'Belgium', 'Switzerland', 'Luxembourg', 'Germany', 'Italy', 'Spain', 'Portugal',
        'United Kingdom', 'Netherlands', 'Austria', 'Denmark', 'Sweden', 'Norway', 'Finland',
        'United States', 'Canada', 'Australia', 'New Zealand', 'Japan', 'China', 'South Korea',
        'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
        'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia', 'Bosnia and Herzegovina',
        'Brazil', 'Bulgaria', 'Cambodia', 'Canada', 'Chile', 'China', 'Colombia', 'Croatia',
        'Cyprus', 'Czech Republic', 'Denmark', 'Ecuador', 'Egypt', 'Estonia', 'Finland',
        'France', 'Georgia', 'Germany', 'Greece', 'Hong Kong', 'Hungary', 'Iceland', 'India',
        'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan',
        'Kazakhstan', 'Kuwait', 'Latvia', 'Lebanon', 'Liechtenstein', 'Lithuania', 'Luxembourg',
        'Malaysia', 'Malta', 'Mexico', 'Monaco', 'Morocco', 'Netherlands', 'New Zealand',
        'Norway', 'Oman', 'Pakistan', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
        'Romania', 'Russia', 'Saudi Arabia', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia',
        'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Taiwan',
        'Thailand', 'Tunisia', 'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
        'United States', 'Uruguay', 'Venezuela', 'Vietnam'
      ];

      return countries.map(country => 
        `<option value="${this.escapeHtml(country)}">${this.escapeHtml(country)}</option>`
      ).join('');
    }

    renderStickyRecap() {
      const recap = document.createElement('div');
      recap.className = 'configurator-sticky';
      
      // Check if there are configurable zones
      const hasConfigurableZones = this.configurator.config.zones && this.configurator.config.zones.length > 0;
      
      recap.innerHTML = `
        <div class="configurator-sticky__content">
          ${hasConfigurableZones ? `<div class="configurator-sticky__title">${this.getTranslation('recap.title')}</div>
          <div class="configurator-sticky__selections"></div>
          <div class="configurator-sticky__total">
            <span class="configurator-sticky__total-label">${this.getTranslation('recap.total')}:</span>
            <span class="configurator-sticky__total-amount">-</span>
          </div>` : ''}
          <div class="configurator-sticky__actions">
            <div class="configurator-sticky__quantity">
              <button type="button" class="configurator-sticky__quantity-btn" data-action="decrease" aria-label="Decrease quantity">-</button>
              <input type="number" class="configurator-sticky__quantity-input" value="1" min="1" aria-label="Quantity" readonly>
              <button type="button" class="configurator-sticky__quantity-btn" data-action="increase" aria-label="Increase quantity">+</button>
            </div>
            <button class="configurator-sticky__cta configurator-sticky__cta--quote button button--primary">
              ${this.getTranslation('recap.validate_quote')}
            </button>
            <button class="configurator-sticky__cta configurator-sticky__cta--cart button button--secondary">
              ${this.getTranslation('recap.add_to_cart')}
            </button>
          </div>
          <div class="configurator-sticky__validation-message" style="display: none;"></div>
        </div>
      `;

      // Quantity controls
      const quantityInput = recap.querySelector('.configurator-sticky__quantity-input');
      const decreaseBtn = recap.querySelector('[data-action="decrease"]');
      const increaseBtn = recap.querySelector('[data-action="increase"]');
      
      const refreshTotalOnQuantityChange = () => {
        this._updateTotalDisplay();
      };

      decreaseBtn.addEventListener('click', () => {
        const currentQty = parseInt(quantityInput.value, 10) || 1;
        if (currentQty > 1) {
          quantityInput.value = currentQty - 1;
          refreshTotalOnQuantityChange();
        }
      });
      
      increaseBtn.addEventListener('click', () => {
        const currentQty = parseInt(quantityInput.value, 10) || 1;
        quantityInput.value = currentQty + 1;
        refreshTotalOnQuantityChange();
      });

      if (quantityInput) {
        quantityInput.addEventListener('change', refreshTotalOnQuantityChange);
        quantityInput.addEventListener('input', refreshTotalOnQuantityChange);
      }

      // Validate & Get Quote button
      const quoteButton = recap.querySelector('.configurator-sticky__cta--quote');
      if (quoteButton) {
        quoteButton.addEventListener('click', () => {
          const quantity = parseInt(quantityInput.value) || 1;
          this.configurator.requestQuote(quantity);
        });
      }

      // Add to Cart button
      const addToCartButton = recap.querySelector('.configurator-sticky__cta--cart');
      if (addToCartButton) {
        addToCartButton.addEventListener('click', () => {
          const quantity = parseInt(quantityInput.value) || 1;
          this.configurator.addToCart(quantity);
        });
      }

      return recap;
    }

    updateSelection(zoneId, material) {
      const zone = this.rootEl.querySelector(`[data-zone-id="${zoneId}"]`);
      if (!zone) return;

      // Find the zone config to re-render it properly
      const zoneConfig = this.configurator.config.zones.find(z => z.id === zoneId);
      if (!zoneConfig) return;

      // Get the options container
      const optionsContainer = zone.querySelector('.configurator-zone__options');
      if (!optionsContainer) return;

      // Get all option elements (excluding the "view all" button)
      const optionElements = Array.from(optionsContainer.querySelectorAll('.configurator-option'));
      const viewAllBtn = optionsContainer.querySelector('.configurator-zone__view-all');
      
      // Check if selected material is in the visible options
      const selectedOption = optionElements.find(opt => opt.dataset.optionId === material.id);
      
      if (selectedOption) {
        // Selected option is already visible, just reorder
        // Remove all options from container
        optionElements.forEach(opt => opt.remove());
        
        // Update selection state
        optionElements.forEach(opt => {
          opt.classList.remove('configurator-option--selected');
          if (opt.dataset.optionId === material.id) {
            opt.classList.add('configurator-option--selected');
          }
        });
        
        // Reorder: put selected first, then others (limit to 4 total visible)
        const otherOptions = optionElements.filter(opt => opt.dataset.optionId !== material.id);
        
        // Re-insert: selected first, then others
        optionsContainer.insertBefore(selectedOption, optionsContainer.firstChild);
        otherOptions.slice(0, 3).forEach(opt => {
          optionsContainer.insertBefore(opt, selectedOption.nextSibling);
        });
        
        // Re-insert remaining options if any (they'll be hidden by CSS)
        otherOptions.slice(3).forEach(opt => {
          if (viewAllBtn) {
            optionsContainer.insertBefore(opt, viewAllBtn);
          } else {
            optionsContainer.appendChild(opt);
          }
        });
      } else {
        // Selected option is not in visible 4, need to re-render the zone card
        // Get all options from config
        const allOptions = Array.isArray(zoneConfig.options) ? zoneConfig.options : [];
        
        // Reorder options to put selected material first
        let orderedOptions = [...allOptions];
        const selectedIndex = orderedOptions.findIndex(opt => opt && opt.id === material.id);
        if (selectedIndex > 0) {
          const [selected] = orderedOptions.splice(selectedIndex, 1);
          orderedOptions.unshift(selected);
        }
        
        // Clear container (but keep view all button reference)
        optionsContainer.innerHTML = '';
        
        // Render first 4 options (with selected first)
        const visibleOptions = orderedOptions.slice(0, 4);
        visibleOptions.forEach((option, index) => {
          if (!option || !option.id) {
            return;
          }
          const optionEl = this.renderOption(option, zoneId, true);
          // Mark as selected if it's the selected material
          if (option.id === material.id) {
            optionEl.classList.add('configurator-option--selected');
          }
          optionsContainer.appendChild(optionEl);
        });
        
        // Re-add "View all" button if needed
        if (allOptions.length > 4) {
          if (viewAllBtn) {
            optionsContainer.appendChild(viewAllBtn);
          } else {
            const newViewAllBtn = document.createElement('button');
            newViewAllBtn.className = 'configurator-zone__view-all';
            newViewAllBtn.textContent = this.getTranslation('zones.view_all');
            newViewAllBtn.addEventListener('click', () => this.openModal(zoneConfig));
            optionsContainer.appendChild(newViewAllBtn);
          }
        }
      }

      // Update sticky recap
      this.updateStickyRecap();
    }

    updateStickyRecap() {
      const selectionsEl = this.rootEl.querySelector('.configurator-sticky__selections');
      if (!selectionsEl) return;

      selectionsEl.innerHTML = '';

      this.configurator.config.zones.forEach(zone => {
        const material = this.configurator.selectedMaterials[zone.id];
        const zoneName = this.configurator.zoneManager.getZoneName(zone);
        
        const selection = document.createElement('div');
        selection.className = 'configurator-sticky__selection';
        
        if (material) {
          const materialName = this.locale === 'fr' ? material.name_fr : material.name_en;
          selection.innerHTML = `
            <span class="configurator-sticky__selection-zone">${this.escapeHtml(zoneName)}:</span>
            <span class="configurator-sticky__selection-material">${this.escapeHtml(materialName)}</span>
          `;
        } else {
          selection.className = 'configurator-sticky__selection configurator-sticky__selection--missing';
          selection.innerHTML = `
            <span class="configurator-sticky__selection-zone">${this.escapeHtml(zoneName)}:</span>
            <span class="configurator-sticky__selection-material configurator-sticky__selection-material--pending">${this.getTranslation('zones.select_material')}</span>
          `;
          // Make it clickable to scroll to zone
          selection.style.cursor = 'pointer';
          selection.setAttribute('role', 'button');
          selection.setAttribute('tabindex', '0');
          selection.setAttribute('aria-label', `${this.escapeHtml(zoneName)} - ${this.getTranslation('zones.select_material')}`);
          selection.addEventListener('click', () => {
            const zoneEl = this.rootEl.querySelector(`[data-zone-id="${zone.id}"]`);
            if (zoneEl) {
              zoneEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              zoneEl.classList.add('configurator-zone--pulse');
              setTimeout(() => {
                zoneEl.classList.remove('configurator-zone--pulse');
              }, 2000);
            }
          });
          // Keyboard support
          selection.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selection.click();
            }
          });
        }

        selectionsEl.appendChild(selection);
      });
    }

    updateTotal(priceData) {
      const totalEl = this.rootEl.querySelector('.configurator-sticky__total-amount');
      if (!totalEl) return;

      // Ensure priceData is valid
      if (!priceData || typeof priceData.total !== 'number') {
        console.warn('[Configurator] Invalid priceData in updateTotal:', priceData);
        return;
      }

      this.lastUnitTotal = priceData.total;
      if (this.configurator && this.configurator._priceLog) {
        this.configurator._priceLog('6.Display', 'updateTotal: stored lastUnitTotal (per unit)', { lastUnitTotalCents: priceData.total, base: priceData.base, deltas: priceData.deltas });
      }
      this._updateTotalDisplay();
    }

    _updateTotalDisplay() {
      const totalEl = this.rootEl.querySelector('.configurator-sticky__total-amount');
      const quantityInput = this.rootEl.querySelector('.configurator-sticky__quantity-input');
      if (!totalEl) return;

      const quantity = (quantityInput && parseInt(quantityInput.value, 10)) || 1;
      const totalCents = this.lastUnitTotal * quantity;

      var displayed;
      if (this.lastUnitTotal > 0 && totalCents > 0) {
        var format = (window.theme && window.theme.moneyFormat) || (window.Shopify && window.Shopify.money_format) || '{{amount}}';
        if (typeof formatMoney === 'function') {
          displayed = formatMoney(totalCents, format);
        } else if (window.Shopify && window.Shopify.formatMoney) {
          displayed = window.Shopify.formatMoney(totalCents, format);
        } else {
          const amount = (totalCents / 100).toFixed(2);
          displayed = '€' + amount.replace('.', ',');
        }
        totalEl.textContent = displayed;
      } else {
        displayed = '-';
        totalEl.textContent = displayed;
      }
      if (this.configurator && this.configurator._priceLog) {
        this.configurator._priceLog('6.Display', '_updateTotalDisplay (quantity × unit total)', {
          lastUnitTotalCents: this.lastUnitTotal,
          quantity,
          totalCents,
          displayed,
          note: 'Display uses theme money format (currency).'
        });
      }
    }

    setAddToCartEnabled(enabled) {
      // Only affect the "Add to Cart" CTA.
      const cartCta = this.rootEl.querySelector('.configurator-sticky__cta--cart');
      if (cartCta) {
        cartCta.disabled = !enabled;
        cartCta.setAttribute('aria-disabled', !enabled);
      }
    }

    showValidationMessage(type, missingZones = []) {
      const messageEl = this.rootEl.querySelector('.configurator-sticky__validation-message');
      if (!messageEl) return;

      let message = this.getTranslation(`validation.${type}`);
      
      // Add details about missing zones if provided
      if (type === 'incomplete' && missingZones && missingZones.length > 0) {
        const zoneNames = missingZones.map(zone => {
          const zoneName = this.configurator.zoneManager.getZoneName(zone);
          return zoneName;
        }).join(', ');
        
        const detailMessage = this.locale === 'fr' 
          ? ` : ${zoneNames}`
          : ` : ${zoneNames}`;
        
        message += detailMessage;
      }
      
      messageEl.textContent = message;
      messageEl.style.display = 'block';
      messageEl.classList.add('configurator-sticky__validation-message--visible');
    }

    hideValidationMessage() {
      const messageEl = this.rootEl.querySelector('.configurator-sticky__validation-message');
      if (messageEl) {
        messageEl.style.display = 'none';
        messageEl.classList.remove('configurator-sticky__validation-message--visible');
      }
    }

    showError(message) {
      // Could show toast or inline error
      alert(message); // Simple fallback
    }

    getTranslation(key) {
      // Try to get from Shopify theme translations
      // Shopify exposes translations via window.Shopify?.locales or similar
      // For now, use fallback translations
      
      // Fallback translations
      const fallbacks = {
        'zones.select_material': this.locale === 'fr' ? 'Sélectionner un matériau' : 'Select material',
        'zones.selected': this.locale === 'fr' ? 'Sélectionné' : 'Selected',
        'zones.view_all': this.locale === 'fr' ? 'Voir tout' : 'View all options',
        'modal.close': this.locale === 'fr' ? 'Fermer' : 'Close',
        'modal.search_placeholder': this.locale === 'fr' ? 'Rechercher des matériaux...' : 'Search materials...',
        'modal.no_results': this.locale === 'fr' ? 'Aucun matériau trouvé' : 'No materials found',
        'modal.title': this.locale === 'fr' ? 'Sélectionner un matériau' : 'Select material',
        'recap.title': this.locale === 'fr' ? 'Votre sélection' : 'Your selection',
        'recap.total': this.locale === 'fr' ? 'Total' : 'Total',
        'recap.add_to_cart': this.locale === 'fr' ? 'Ajouter au panier' : 'Add to cart',
        'recap.validate_quote': this.locale === 'fr' ? 'Valider & Obtenir Devis' : 'Validate & Get Quote',
        'recap.quote_success_title': this.locale === 'fr' ? 'Demande envoyée avec succès' : 'Request sent successfully',
        'recap.quote_success_message': this.locale === 'fr' ? 'Merci ! Un conseiller design vous contactera rapidement.' : 'Thank you! A design advisor will contact you shortly.',
        'recap.quote_error': this.locale === 'fr' ? 'Une erreur s\'est produite. Veuillez réessayer.' : 'An error occurred. Please try again.',
        'quote_modal.title': this.locale === 'fr' ? 'Demander un devis' : 'Request a Quote',
        'quote_modal.first_name': this.locale === 'fr' ? 'Prénom' : 'First Name',
        'quote_modal.last_name': this.locale === 'fr' ? 'Nom' : 'Last Name',
        'quote_modal.email': this.locale === 'fr' ? 'Email' : 'Email',
        'quote_modal.phone': this.locale === 'fr' ? 'Téléphone' : 'Phone',
        'quote_modal.company': this.locale === 'fr' ? 'Entreprise' : 'Company',
        'quote_modal.country': this.locale === 'fr' ? 'Pays de destination' : 'Destination Country',
        'quote_modal.city': this.locale === 'fr' ? 'Ville' : 'City',
        'quote_modal.note': this.locale === 'fr' ? 'Note' : 'Note',
        'quote_modal.submit': this.locale === 'fr' ? 'Envoyer la demande' : 'Send Request',
        'quote_modal.cancel': this.locale === 'fr' ? 'Annuler' : 'Cancel',
        'quote_modal.required': this.locale === 'fr' ? 'Champ obligatoire' : 'Required field',
        'quote_modal.email_invalid': this.locale === 'fr' ? 'Veuillez saisir une adresse e-mail valide.' : 'Please enter a valid email address.',
        'help_block.title': this.locale === 'fr' ? 'Besoin d\'aide pour choisir les finitions ?' : 'Need help choosing finishes?',
        'help_block.subtitle': this.locale === 'fr' ? 'Nos conseillers design valident chaque configuration avant production.' : 'Our design advisors review every configuration before production.',
        'help_block.open_chat': this.locale === 'fr' ? 'Ouvrir le chat' : 'Open chat',
        'validation.incomplete': this.locale === 'fr' ? 'Veuillez sélectionner les matériaux pour' : 'Please select all materials for',
        'validation.pricing_unavailable': this.locale === 'fr' ? 'Tarification indisponible pour cette configuration' : 'Pricing unavailable for this configuration'
      };

      return fallbacks[key] || key;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Initialize on DOM ready
  function init() {
    const configurators = document.querySelectorAll('.onohome-configurator');
    
    configurators.forEach((el) => {
      if (!el.dataset.initialized) {
        el.dataset.initialized = 'true';
        new ProductConfigurator(el);
      }
    });
  }

  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when section is loaded in theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const configurator = e.target.querySelector('.onohome-configurator');
    if (configurator) {
      configurator.dataset.initialized = 'false';
      init();
    }
  });

})();

